import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { TextField } from "../ui/components/TextField";
import { Select } from "../ui/components/Select";
import { Loader } from "../ui/components/Loader";
import { IconWithBackground } from "../ui/components/IconWithBackground";
import { Tooltip } from "../ui/components/Tooltip";
import { Dialog } from "../ui/components/Dialog";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherAlertTriangle } from "@subframe/core";
import { FeatherCalendar } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import { FeatherLock } from "@subframe/core";
import { FeatherTrash2 } from "@subframe/core";
import { truncateText } from "../shared/utils/formatters";
import {
  isApAccountType,
  isArAccountType,
} from "../shared/utils/accountTypeHelpers";

import {
  journalsAPI,
  fetchCurrencies,
  Currency,
  JournalAttachment,
  Journal,
} from "../services/api";
import { LocationPicker } from "../shared/presentation/components/LocationPicker";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";
import { getAllCustomers, getCustomerById } from "../services/customerApi";
import { getAllVendors, getVendorById } from "../services/vendorApi";
import {
  SearchableDropdown,
  GroupedItems,
  ErrorBanner,
  useToast,
  ReviewStatusBanner,
  ApprovedChangesModal,
  ChangeDetails,
  ReviewRequestModal,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
  AccountNameDropdown,
} from "../shared/presentation/components";
import {
  useErrorHandling,
  useDropdownCache,
  useJournalReviewData,
  useEntityReviewStatus,
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../shared/presentation/hooks";
import { dateToMonthYear } from "../shared/utils/date";
import { ACTION_TYPE_MONTH_CLOSE } from "../shared/utils/reviewWorkflow";
import { useAuth } from "../features/auth/presentation/hooks/useAuth";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import ConfirmEditJournalModal, {
  JournalChange,
} from "../components/ConfirmEditJournalModal";
import {
  getStoredAccountingBackend,
  getPostButtonLabel,
  getBackendName,
} from "../config/accounting";

interface JournalLineItemForm {
  id: string;
  account_type: string;
  account_id: string;
  account_name: string;
  description: string;
  contact_id: string;
  contact_name: string;
  customer_or_vendor: "customer" | "vendor" | "";
  reference_id: string;
  reference_display: string;
  /** Balance of selected bill/invoice (for validation: line amount must not exceed this) */
  reference_balance?: number;
  debit: string;
  credit: string;
}

const AP_AR_DESCRIPTION_MAX_LENGTH = 95;

const EditJournalPage: React.FC = () => {
  const navigate = useNavigate();
  const { journalId } = useParams<{ journalId: string }>();
  const { user } = useAuth();

  // Form state
  const [date, setDate] = useState("");
  const [journalNumber, setJournalNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [currencyId, setCurrencyId] = useState("");
  // ZOUR-65 F4: the GST registration this journal is filed against.
  const [locationId, setLocationId] = useState<string | null>(null);
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [notes, setNotes] = useState("");
  const [reportingMethod, setReportingMethod] = useState<string>("both");
  const [zohoJournalId, setZohoJournalId] = useState<string | null>(null);
  const [tallyRemoteId, setTallyRemoteId] = useState<string | null>(null);
  const [tallyMasterId, setTallyMasterId] = useState<number | null>(null);
  const [status, setStatus] = useState<string>("under_review");
  // ZOUR-65 F4: which of our GST registrations this journal belongs to.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
  } = useActiveLocations();
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);

  // Recurring journal state
  const [isRecurring, setIsRecurring] = useState<string>("no");
  const [recurringJournalName, setRecurringJournalName] = useState("");
  const [recurringFrequency, setRecurringFrequency] = useState("");
  const [recurringInterval, setRecurringInterval] = useState<number | "">("");
  const [recurringStartDate, setRecurringStartDate] = useState("");
  const [recurringEndDate, setRecurringEndDate] = useState("");
  const [recurringNeverExpires, setRecurringNeverExpires] = useState(false);

  // Line items state
  const [lineItems, setLineItems] = useState<JournalLineItemForm[]>([]);
  const showBillInvoiceColumn = useMemo(
    () =>
      lineItems.some(
        (item) =>
          item.contact_id &&
          (isApAccountType(item.account_type || "") ||
            isArAccountType(item.account_type || "")),
      ),
    [lineItems],
  );
  const isApArJournal = useMemo(
    () =>
      lineItems.some(
        (item) =>
          isApAccountType(item.account_type || "") ||
          isArAccountType(item.account_type || ""),
      ),
    [lineItems],
  );

  // Dropdown options state
  // Cache for selected contacts (for display purposes)
  const [contactsCache, setContactsCache] = useState<Map<string, any>>(
    new Map(),
  );
  const [currencies, setCurrencies] = useState<Currency[]>([]);

  const [isResolvingLineItemAccountTypes, setIsResolvingLineItemAccountTypes] =
    useState(false);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  // Error handling with new hook
  const {
    errors,
    setFieldError,
    setFieldErrors,
    setApiErrors,
    clearFieldError,
    hasLineItemError,
  } = useErrorHandling();

  // Dropdown cache hook
  const { getCached, setCached, clearCache } = useDropdownCache();

  // Journal review data hook
  const {
    canUpdate,
    canUpdateAfterPush,
    canPushToZoho,
    canCreateRecurring,
    canDeleteAfterPush,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForPushToZohoWithoutAttachment,
    needsReviewForCreateRecurring,
    needsReviewForDeleteAfterPush,
    hasApprovedReview,
    isDataLoading: isReviewDataLoading,
    isPostedToZoho: isPostedToZohoFromHook,
  } = useJournalReviewData({
    moduleCode: MODULE_CODES.JOURNAL,
    entityType: "journal",
    documentId: journalId || null,
    fetchDocument: async (id: string) => {
      const response = await journalsAPI.getJournalById(id);
      return response.data;
    },
    isPostedToZoho: (doc: any) =>
      !!doc?.zoho_journalid ||
      !!doc?.tally_remote_id ||
      !!doc?.tally_master_id ||
      !!doc?.posted_to_tally_date,
    initialDocument: null,
  });

  // "Posted to accounting" — Zoho or Tally. Hook name kept for permission/review wiring.
  const isPostedToTally = !!tallyRemoteId || tallyMasterId != null;
  const isPostedToZoho =
    isPostedToZohoFromHook || !!zohoJournalId || isPostedToTally;
  const isPostedToAccounting = isPostedToZoho;
  const accountingBackend = getStoredAccountingBackend();

  // Review status
  const { review, hasPendingReview } = useEntityReviewStatus(
    journalId ? "journal" : null,
    journalId ? parseInt(journalId) : null,
  );

  // Closed month state
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);
  const [pendingClosedMonthAction, setPendingClosedMonthAction] = useState<
    "update" | "publish" | "delete" | null
  >(null);

  // Review modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingReviewAction, setPendingReviewAction] = useState<
    "update" | "publish" | null
  >(null);
  const [pendingUpdateAllFuture, setPendingUpdateAllFuture] = useState(false);
  const [reviewChangeDetails, setReviewChangeDetails] =
    useState<ChangeDetails | null>(null);
  const [reviewActionCode, setReviewActionCode] = useState<string>("");

  // Closed month check (journal date)
  const journalMonth = date ? dateToMonthYear(date) : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(journalMonth);
  const isAdminOrCFO = useIsAdminOrCFO();

  // Approved changes modal state
  const [approvedChangesModalOpen, setApprovedChangesModalOpen] =
    useState(false);

  // State for showing approved banner temporarily
  const [showApprovedBanner, setShowApprovedBanner] = useState(false);
  const approvedBannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pageError, setPageError] = useState<string | null>(null);

  // Toast notifications
  const { showSuccess, showError } = useToast();

  // File upload state
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<
    JournalAttachment[]
  >([]);

  // Confirm edit modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUpdateType, setPendingUpdateType] = useState<
    "update" | "publish" | null
  >(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track original values for change detection
  const [originalData, setOriginalData] = useState<{
    date: string;
    referenceNumber: string;
    currencyId: string;
    // ZOUR-65 F4: without this, changing ONLY the location leaves
    // `detectChanges()` empty and the Update button disabled — the edit is
    // real but unsaveable.
    locationId: string | null;
    notes: string;
    reportingMethod: string;
    lineItems: JournalLineItemForm[];
  } | null>(null);

  // Flag to ensure originalData is only updated once after initial load
  const [originalDataFinalized, setOriginalDataFinalized] = useState(false);

  // Store original Journal JSON for accurate change detection (compares API payload format)
  const [originalJournalJson, setOriginalJournalJson] =
    useState<Journal | null>(null);

  // Helper function to format account type display
  const formatAccountType = (accountType: string): string => {
    return accountType.replace(/_/g, " ").toUpperCase();
  };

  // Helper function to determine if account requires vendor (Accounts Payable)
  const shouldShowOnlyVendors = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts payable") ||
      accountName.toLowerCase().includes("account payable")
    );
  };

  // Helper function to determine if account requires customer (Accounts Receivable)
  const shouldShowOnlyCustomers = (accountName: string): boolean => {
    return (
      accountName.toLowerCase().includes("accounts receivable") ||
      accountName.toLowerCase().includes("account receivable")
    );
  };

  // Helper function to check if contact is required for the account
  const isContactRequired = (accountName: string): boolean => {
    return (
      shouldShowOnlyVendors(accountName) || shouldShowOnlyCustomers(accountName)
    );
  };

  // Helper function to check if contact currency matches journal currency
  const isContactCurrencyMatching = (
    contact: any,
    isVendor: boolean,
  ): boolean => {
    if (!currencyId) return true; // If no currency selected, allow all

    if (isVendor) {
      // For vendors, check vendor.currency_id matches journal currency_id
      return contact.currency_id === currencyId;
    } else {
      // For customers, check customer.currency_id matches journal currency_id
      return contact.currency_code === currencyId;
    }
  };

  // Helper function to fetch and build grouped contacts for a line item (async)
  const fetchGroupedContacts = async (
    accountName: string,
    searchTerm: string,
    limit: number,
    offset: number,
    selectedContactId?: string,
    selectedContactName?: string,
    selectedContactType?: "customer" | "vendor",
  ): Promise<{
    items: GroupedItems<any>[];
    hasMore?: boolean;
  }> => {
    // Only cache the initial load (offset 0, no search)
    // Note: We also need to include currencyId in cache key since contacts are filtered by currency
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { accountName, searchTerm, limit, offset, currencyId };

    if (shouldCache) {
      const cached = getCached<{
        items: GroupedItems<any>[];
        hasMore?: boolean;
      }>("contacts", cacheKey);

      if (cached) {
        // Only ensure selected contact is in the cached results on initial load (offset === 0)
        if (
          offset === 0 &&
          selectedContactId &&
          selectedContactName &&
          selectedContactType
        ) {
          // Check if selected contact exists in cached results and find its position
          let targetGroup: GroupedItems<any> | undefined;
          let selectedIndex = -1;

          for (const group of cached.items) {
            selectedIndex = group.items.findIndex((contact: any) => {
              const contactId =
                contact.zoho_customerid || contact.zoho_vendorid;
              return contactId === selectedContactId;
            });
            if (selectedIndex >= 0) {
              targetGroup = group;
              break;
            }
          }

          // If selected contact exists in cache, move it to top for better UX
          if (targetGroup && selectedIndex >= 0) {
            if (selectedIndex > 0) {
              // Remove from current position and prepend to top
              const itemsCopy = [...targetGroup.items];
              const [selectedContact] = itemsCopy.splice(selectedIndex, 1);
              targetGroup.items = [selectedContact, ...itemsCopy];

              return {
                items: cached.items,
                hasMore: cached.hasMore,
              };
            }
            // Already at top, return cached as is
            return cached;
          }

          // Selected contact not found in cache
          if (!targetGroup || selectedIndex < 0) {
            // Check contacts cache first (pre-fetched contacts)
            const cachedContact = contactsCache.get(selectedContactId);
            if (cachedContact) {
              const groupLabel =
                selectedContactType === "customer" ? "Customers" : "Vendors";
              let targetGroup = cached.items.find(
                (g: GroupedItems<any>) => g.groupLabel === groupLabel,
              );

              if (!targetGroup) {
                targetGroup = { groupLabel, items: [], hasMore: false };
                cached.items.push(targetGroup);
              }

              // Remove contact from its current position if it exists, then prepend
              const contactId =
                cachedContact.zoho_customerid || cachedContact.zoho_vendorid;
              targetGroup.items = targetGroup.items.filter(
                (c: any) =>
                  (c.zoho_customerid || c.zoho_vendorid) !== contactId,
              );
              targetGroup.items = [cachedContact, ...targetGroup.items];

              return {
                items: cached.items,
                hasMore: cached.hasMore,
              };
            }

            // Try to fetch from backend
            try {
              let selectedContact: any = null;
              if (selectedContactType === "customer") {
                const response = await getCustomerById(selectedContactId);
                selectedContact = response.data;
              } else if (selectedContactType === "vendor") {
                const response = await getVendorById(selectedContactId);
                selectedContact = response.data;
              }

              if (selectedContact) {
                // Store in cache for future use
                setContactsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedContactId, selectedContact);
                  return newCache;
                });

                const groupLabel =
                  selectedContactType === "customer" ? "Customers" : "Vendors";
                let targetGroup = cached.items.find(
                  (g: GroupedItems<any>) => g.groupLabel === groupLabel,
                );

                if (!targetGroup) {
                  targetGroup = { groupLabel, items: [], hasMore: false };
                  cached.items.push(targetGroup);
                }

                // Remove contact from its current position if it exists, then prepend
                const contactId =
                  selectedContact.zoho_customerid ||
                  selectedContact.zoho_vendorid;
                targetGroup.items = targetGroup.items.filter(
                  (c: any) =>
                    (c.zoho_customerid || c.zoho_vendorid) !== contactId,
                );
                targetGroup.items = [selectedContact, ...targetGroup.items];

                return {
                  items: cached.items,
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected contact from backend, using synthetic item:",
                error,
              );
            }

            // Fallback: Create synthetic contact
            const syntheticContact: any =
              selectedContactType === "customer"
                ? {
                    zoho_customerid: selectedContactId,
                    customer_name: selectedContactName,
                    currency_code: currencyId,
                  }
                : {
                    zoho_vendorid: selectedContactId,
                    vendor_legal_name: selectedContactName,
                    currency_id: currencyId,
                  };

            const groupLabel =
              selectedContactType === "customer" ? "Customers" : "Vendors";
            let targetGroup = cached.items.find(
              (g: GroupedItems<any>) => g.groupLabel === groupLabel,
            );

            if (!targetGroup) {
              targetGroup = { groupLabel, items: [], hasMore: false };
              cached.items.push(targetGroup);
            }

            // Remove contact from its current position if it exists, then prepend
            targetGroup.items = targetGroup.items.filter(
              (c: any) =>
                (c.zoho_customerid || c.zoho_vendorid) !== selectedContactId,
            );
            targetGroup.items = [syntheticContact, ...targetGroup.items];

            return {
              items: cached.items,
              hasMore: cached.hasMore,
            };
          }
        }
        return cached;
      }
    }

    const showOnlyVendors = shouldShowOnlyVendors(accountName);
    const showOnlyCustomers = shouldShowOnlyCustomers(accountName);

    const groups: GroupedItems<any>[] = [];
    let overallHasMore = false;

    // Fetch customers if applicable
    if (!showOnlyVendors) {
      try {
        const customersResponse = await getAllCustomers({
          search: searchTerm,
          limit,
          offset,
        });
        const filteredCustomers = customersResponse.data.filter((customer) =>
          isContactCurrencyMatching(customer, false),
        );
        // const filteredCustomers = customersResponse.data;
        if (filteredCustomers.length > 0) {
          groups.push({
            groupLabel: "Customers",
            items: filteredCustomers,
            hasMore: customersResponse.pagination?.has_more || false,
          });
        }
        // Track hasMore even if filtered results are empty
        // because next page might have matching items
        if (customersResponse.pagination?.has_more) {
          overallHasMore = true;
        }
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    }

    // Fetch vendors if applicable
    if (!showOnlyCustomers) {
      try {
        const vendorsResponse = await getAllVendors({
          search: searchTerm,
          limit,
          offset,
        });
        const filteredVendors = vendorsResponse.data.filter((vendor) =>
          isContactCurrencyMatching(vendor, true),
        );
        // const filteredVendors = vendorsResponse.data;
        if (filteredVendors.length > 0) {
          groups.push({
            groupLabel: "Vendors",
            items: filteredVendors,
            hasMore: vendorsResponse.pagination?.has_more || false,
          });
        }
        // Track hasMore even if filtered results are empty
        // because next page might have matching items
        if (vendorsResponse.pagination?.has_more) {
          overallHasMore = true;
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    }

    // Only ensure selected contact is in results on initial load (offset === 0)
    if (
      offset === 0 &&
      selectedContactId &&
      selectedContactName &&
      selectedContactType
    ) {
      // Check if selected contact exists in results and find its position
      let targetGroup: GroupedItems<any> | undefined;
      let selectedIndex = -1;

      for (const group of groups) {
        selectedIndex = group.items.findIndex((contact: any) => {
          const contactId = contact.zoho_customerid || contact.zoho_vendorid;
          return contactId === selectedContactId;
        });
        if (selectedIndex >= 0) {
          targetGroup = group;
          break;
        }
      }

      // If selected contact exists in results, move it to top for better UX
      if (targetGroup && selectedIndex >= 0) {
        if (selectedIndex > 0) {
          // Remove from current position and prepend to top
          const itemsCopy = [...targetGroup.items];
          const [selectedContact] = itemsCopy.splice(selectedIndex, 1);
          targetGroup.items = [selectedContact, ...itemsCopy];
        }
        // Already at top or moved to top, continue
      } else if (!targetGroup || selectedIndex < 0) {
        // Selected contact not found in results
        // Check contacts cache first
        const cachedContact = contactsCache.get(selectedContactId);
        if (cachedContact) {
          const groupLabel =
            selectedContactType === "customer" ? "Customers" : "Vendors";
          let targetGroup = groups.find((g) => g.groupLabel === groupLabel);

          if (!targetGroup) {
            targetGroup = { groupLabel, items: [], hasMore: false };
            groups.push(targetGroup);
          }

          // Remove contact from its current position if it exists, then prepend
          const contactId =
            cachedContact.zoho_customerid || cachedContact.zoho_vendorid;
          targetGroup.items = targetGroup.items.filter(
            (c: any) => (c.zoho_customerid || c.zoho_vendorid) !== contactId,
          );
          targetGroup.items = [cachedContact, ...targetGroup.items];
        } else {
          // Try to fetch from backend
          try {
            let selectedContact: any = null;
            if (selectedContactType === "customer") {
              const response = await getCustomerById(selectedContactId);
              selectedContact = response.data;
            } else if (selectedContactType === "vendor") {
              const response = await getVendorById(selectedContactId);
              selectedContact = response.data;
            }

            if (selectedContact) {
              // Check currency match
              const currencyMatches =
                selectedContactType === "customer"
                  ? selectedContact.currency_code === currencyId
                  : selectedContact.currency_id === currencyId;

              if (currencyMatches || !currencyId) {
                // Store in cache for future use
                setContactsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedContactId, selectedContact);
                  return newCache;
                });

                const groupLabel =
                  selectedContactType === "customer" ? "Customers" : "Vendors";
                let targetGroup = groups.find(
                  (g) => g.groupLabel === groupLabel,
                );

                if (!targetGroup) {
                  targetGroup = { groupLabel, items: [], hasMore: false };
                  groups.push(targetGroup);
                }

                // Remove contact from its current position if it exists, then prepend
                const contactId =
                  selectedContact.zoho_customerid ||
                  selectedContact.zoho_vendorid;
                targetGroup.items = targetGroup.items.filter(
                  (c: any) =>
                    (c.zoho_customerid || c.zoho_vendorid) !== contactId,
                );
                // Prepend selected contact to the group
                targetGroup.items = [selectedContact, ...targetGroup.items];
              }
            }
          } catch (error) {
            console.warn(
              "Could not fetch selected contact from backend, using synthetic item:",
              error,
            );
            // Fallback: Create synthetic contact
            const syntheticContact: any =
              selectedContactType === "customer"
                ? {
                    zoho_customerid: selectedContactId,
                    customer_name: selectedContactName,
                    currency_code: currencyId,
                  }
                : {
                    zoho_vendorid: selectedContactId,
                    vendor_legal_name: selectedContactName,
                    currency_id: currencyId,
                  };

            const groupLabel =
              selectedContactType === "customer" ? "Customers" : "Vendors";
            let targetGroup = groups.find((g) => g.groupLabel === groupLabel);

            if (!targetGroup) {
              targetGroup = { groupLabel, items: [], hasMore: false };
              groups.push(targetGroup);
            }

            // Remove contact from its current position if it exists, then prepend
            targetGroup.items = targetGroup.items.filter(
              (c: any) =>
                (c.zoho_customerid || c.zoho_vendorid) !== selectedContactId,
            );
            targetGroup.items = [syntheticContact, ...targetGroup.items];
          }
        }
      }
    } else if (offset > 0 && selectedContactId) {
      // For pagination loads, ensure selected contact is not duplicated
      // Remove selected contact from any group if it exists (it should be at top from initial load)
      const contactIdToRemove = selectedContactId;
      for (const group of groups) {
        const originalLength = group.items.length;
        group.items = group.items.filter((contact: any) => {
          const contactId = contact.zoho_customerid || contact.zoho_vendorid;
          return contactId !== contactIdToRemove;
        });
        // If we removed a duplicate, log it for debugging
        if (group.items.length < originalLength) {
          console.log(
            `Removed duplicate selected contact from ${group.groupLabel} during pagination`,
          );
        }
      }
    }

    const result = {
      items: groups,
      hasMore: overallHasMore,
    };

    // Cache only initial loads
    if (shouldCache) {
      setCached("contacts", cacheKey, result);
    }

    return result;
  };

  // Helper to get contact display name
  const getContactDisplayName = (contactId: string, item: any): string => {
    if (!contactId) return "Select contact";

    // Use contact_name from item if available
    if (item.contact_name) return item.contact_name;

    // Check cache
    const cached = contactsCache.get(contactId);
    if (cached) {
      return (
        cached.customer_name || cached.vendor_legal_name || "Select contact"
      );
    }

    return "Select contact";
  };

  // Helper function to extract meaningful error message
  // Helper function to check if a date is the first day of a month
  const isFirstDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getDate() === 1;
  };

  // Helper function to check if a date is the last day of a month
  const isLastDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return nextDay.getDate() === 1;
  };

  // Helper function to check if a date is end of quarter (Mar 31, Jun 30, Sep 30, Dec 31)
  const isEndOfQuarter = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();

    return (
      (month === 2 && day === 31) || // March 31
      (month === 5 && day === 30) || // June 30
      (month === 8 && day === 30) || // September 30
      (month === 11 && day === 31) // December 31
    );
  };

  // Helper function to check if a date is end of year (Dec 31)
  const isEndOfYear = (dateStr: string): boolean => {
    const date = new Date(dateStr);
    return date.getMonth() === 11 && date.getDate() === 31;
  };

  // Helper function to validate start date based on frequency
  const isValidStartDate = (dateStr: string, frequency: string): boolean => {
    if (!dateStr || !frequency) return true; // Don't validate if empty

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateStr);
    selectedDate.setHours(0, 0, 0, 0);

    switch (frequency) {
      case "start_of_month":
        return isFirstDayOfMonth(dateStr) && selectedDate > today;

      case "end_of_month":
        return isLastDayOfMonth(dateStr) && selectedDate > today;

      case "every_n_days":
        if (!recurringInterval) return true;
        const minDate = new Date(today);
        minDate.setDate(minDate.getDate() + Number(recurringInterval));
        return selectedDate >= minDate;

      case "end_of_year":
        return isEndOfYear(dateStr) && selectedDate > today;

      case "end_of_quarter":
        return isEndOfQuarter(dateStr) && selectedDate > today;

      case "date_of_month":
        return (
          !isFirstDayOfMonth(dateStr) &&
          !isLastDayOfMonth(dateStr) &&
          selectedDate > today
        );

      default:
        return selectedDate >= today;
    }
  };

  // Helper function to get minimum date based on frequency
  const getMinStartDate = (frequency: string): string => {
    const today = new Date();

    switch (frequency) {
      case "every_n_days":
        if (recurringInterval) {
          const minDate = new Date(today);
          minDate.setDate(minDate.getDate() + Number(recurringInterval));
          return minDate.toISOString().split("T")[0];
        }
        return today.toISOString().split("T")[0];

      default:
        // For all other frequencies, tomorrow is the minimum
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split("T")[0];
    }
  };

  // Helper function to get help text for start date based on frequency
  const getStartDateHelpText = (frequency: string): string => {
    switch (frequency) {
      case "start_of_month":
        return "⚠️ You must select the 1st day of a month";
      case "end_of_month":
        return "⚠️ You must select the last day of a month";
      case "end_of_quarter":
        return "⚠️ You must select a quarter-end date: Mar 31, Jun 30, Sep 30, or Dec 31";
      case "end_of_year":
        return "⚠️ You must select December 31st";
      case "date_of_month":
        return "⚠️ You must select any date EXCEPT the 1st or last day of the month";
      case "every_n_days":
        return recurringInterval
          ? `⚠️ Start date must be at least ${recurringInterval} days from today`
          : "Select number of days first";
      default:
        return "";
    }
  };

  // Load journal data
  useEffect(() => {
    const loadJournalData = async () => {
      if (!journalId) {
        setPageError("No journal ID provided");
        setIsPageLoading(false);
        return;
      }

      try {
        setIsPageLoading(true);
        const response = await journalsAPI.getJournalById(journalId);
        const journal = response.data;

        // Store the ORIGINAL Journal JSON for accurate change detection
        setOriginalJournalJson(journal);

        setDate(journal.date === "1900-01-01" ? "" : journal.date);
        setJournalNumber(journal.journal_number || "");
        setStatus(journal.status || "under_review");

        if (journal.template_id && journal.template) {
          setHasTemplate(true);
          setIsRecurring("yes");
          setRecurringJournalName(journal.template.template_name || "");
          setRecurringFrequency(journal.template.recurrence_frequency || "");
          setRecurringInterval(
            journal.template.recurrence_interval
              ? Number(journal.template.recurrence_interval)
              : "",
          );
          const neverExpires =
            journal.template.never_expires === "1" ||
            journal.template.never_expires === 1 ||
            journal.template.never_expires === true;
          setRecurringNeverExpires(neverExpires);
          setRecurringStartDate(journal.template.starts_on || "");
          setRecurringEndDate(journal.template.ends_on || "");
        } else {
          setHasTemplate(false);
          setIsRecurring("no");
        }

        setReferenceNumber(journal.reference_number || "");
        // ZOUR-65 F4: NULL is meaningful — it is the primary location — so the
        // stored value is carried through as-is and the picker resolves it.
        setLocationId(journal.location_id ?? null);
        setCurrencyId(journal.currency_id?.toString() ?? "");
        setCurrencySymbol(journal.currency_symbol ?? "₹");
        setNotes(journal.notes ?? "");
        setReportingMethod(journal.type ?? "both");
        setZohoJournalId(journal.zoho_journalid || null);
        setTallyRemoteId(journal.tally_remote_id || null);
        setTallyMasterId(
          journal.tally_master_id != null && journal.tally_master_id !== ""
            ? Number(journal.tally_master_id)
            : null,
        );

        if (journal.attachment_urls && Array.isArray(journal.attachment_urls)) {
          setExistingAttachments(journal.attachment_urls);
        }

        const formatAccountTypeDisplay = (t: string) =>
          (t || "").replace(/_/g, " ").toUpperCase();

        const formLineItems: JournalLineItemForm[] = journal.line_items
          ? journal.line_items.map((item: any, index: number) => {
              const refId = item.bill_id ?? item.invoice_id ?? "";
              const refDisplay = item.bill_id
                ? `Bill #${item.bill_id}`
                : item.invoice_id
                  ? `Invoice #${item.invoice_id}`
                  : "";
              return {
                id: item.id ? item.id.toString() : (index + 1).toString(),
                account_type: item.account_type ?? "",
                account_id: (item.account_id ?? "").toString(),
                account_name:
                  item.account_name ??
                  formatAccountTypeDisplay(item.account_type) ??
                  "",
                description: item.description ?? "",
                contact_id: item.contact_id ?? "",
                contact_name: item.contact_name ?? "",
                customer_or_vendor: item.customer_or_vendor ?? "",
                reference_id: refId,
                reference_display: refDisplay,
                debit:
                  item.is_debit_or_credit === "debit"
                    ? (item.amount ?? 0).toString()
                    : "",
                credit:
                  item.is_debit_or_credit === "credit"
                    ? (item.amount ?? 0).toString()
                    : "",
              };
            })
          : [];

        setLineItems(formLineItems);

        setOriginalData({
          date: journal.date,
          referenceNumber: journal.reference_number || "",
          currencyId: journal.currency_id?.toString() ?? "",
          locationId: journal.location_id ?? null,
          notes: journal.notes ?? "",
          reportingMethod: journal.type ?? "both",
          lineItems: formLineItems,
        });

        setIsPageLoading(false);
      } catch (error) {
        console.error("Error loading journal:", error);
        setApiErrors(error);
        setPageError("Failed to load journal");
        setIsPageLoading(false);
      }
    };

    loadJournalData();
  }, [journalId, setApiErrors]);

  // Show approved changes modal when approved review exists
  useEffect(() => {
    if (review && review.status === "approved" && !approvedChangesModalOpen) {
      setApprovedChangesModalOpen(true);
    }
  }, [hasApprovedReview, review, approvedChangesModalOpen]);

  // Show approved banner when review is approved (on mount or when review changes to approved)
  useEffect(() => {
    if (review?.status === "approved") {
      setShowApprovedBanner(true);

      // Clear any existing timeout
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }

      // Auto-dismiss after 45 seconds
      approvedBannerTimeoutRef.current = setTimeout(() => {
        setShowApprovedBanner(false);
      }, 45000);
    } else {
      setShowApprovedBanner(false);
    }

    // Cleanup timeout on unmount
    return () => {
      if (approvedBannerTimeoutRef.current) {
        clearTimeout(approvedBannerTimeoutRef.current);
      }
    };
  }, [review?.status]);

  // Apply approved changes from review
  const handleApplyApprovedChanges = (changes: ChangeDetails) => {
    if (!changes.field_changes) return;

    // Apply field changes to form
    changes.field_changes.forEach((fieldChange) => {
      const field = fieldChange.field;
      const newValue = fieldChange.new;

      // Handle journal-level fields
      if (field === "date") {
        setDate(newValue);
      } else if (field === "notes") {
        setNotes(newValue);
      } else if (field === "currency_id") {
        setCurrencyId(newValue);
      } else if (field === "reference_number") {
        setReferenceNumber(newValue);
      } else if (field === "type") {
        setReportingMethod(newValue);
      }
      // Handle line item fields (e.g., "line_items[0].account_name")
      else if (field.startsWith("line_items[")) {
        const match = field.match(/line_items\[(\d+)\]\.(\w+)/);
        if (match) {
          const index = parseInt(match[1]);
          const fieldName = match[2];
          setLineItems((prev) => {
            const updated = [...prev];
            if (updated[index]) {
              if (fieldName === "account_name") {
                updated[index].account_name = newValue;
              } else if (fieldName === "account_id") {
                updated[index].account_id = newValue;
              } else if (fieldName === "description") {
                updated[index].description = newValue;
              } else if (fieldName === "amount") {
                // Determine if debit or credit based on existing value
                if (updated[index].debit) {
                  updated[index].debit = newValue;
                } else {
                  updated[index].credit = newValue;
                }
              }
            }
            return updated;
          });
        }
      }
    });

    showSuccess("Changes from approved review have been applied");
  };

  // Fetch currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error("Error fetching currencies:", error);
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Helper: resolve account_type for a line item that has account_id but no account_type (saved journal).
  // Uses getGLMapByAccountId (one API call) instead of searching across all account types.
  const determineAccountTypeForAccountId = async (
    accountId: string,
  ): Promise<string | null> => {
    try {
      const response = await journalsAPI.getGLMapByAccountId(accountId);
      if (response?.data?.account_type) {
        return response.data.account_type;
      }
    } catch (error) {
      console.error(
        "Error determining account type for account id:",
        accountId,
        error,
      );
    }
    return null;
  };

  // After line items are loaded, determine account types for existing line items (saved/posted journal)
  useEffect(() => {
    if (lineItems.length === 0) return;
    if (lineItems.every((item) => item.account_type !== "")) return; // Already populated

    const itemsNeedingType = lineItems.filter(
      (item) => !item.account_type && item.account_id,
    );
    if (itemsNeedingType.length === 0) return;

    let isMounted = true;
    setIsResolvingLineItemAccountTypes(true);

    const determineAccountTypes = async () => {
      const updates: { id: string; accountType: string }[] = [];
      for (const item of itemsNeedingType) {
        if (!isMounted) break;
        const accountType = await determineAccountTypeForAccountId(
          item.account_id,
        );
        if (accountType) {
          updates.push({ id: item.id, accountType });
        }
      }

      if (!isMounted) {
        setIsResolvingLineItemAccountTypes(false);
        return;
      }
      if (updates.length === 0) {
        setIsResolvingLineItemAccountTypes(false);
        return;
      }

      // Update line items with determined account types
      setLineItems((prevItems) => {
        return prevItems.map((item) => {
          const update = updates.find((u) => u.id === item.id);
          return update ? { ...item, account_type: update.accountType } : item;
        });
      });

      setIsResolvingLineItemAccountTypes(false);
    };

    determineAccountTypes();

    return () => {
      isMounted = false;
      setIsResolvingLineItemAccountTypes(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems.length]); // Only run when line items are first loaded

  // Update original data after all initial loading is complete
  useEffect(() => {
    // If already finalized, never update again
    if (originalDataFinalized) {
      return;
    }

    // Wait for all initial loading to complete
    if (currenciesLoading || isPageLoading) {
      return;
    }

    // Wait for line items to have account types populated
    if (
      lineItems.length === 0 ||
      !lineItems.every((item) => item.account_type !== "")
    ) {
      return;
    }

    // Only update once - ensure we don't overwrite any user edits that may have happened meanwhile
    if (originalData) {
      // Merge only the missing account_type into the captured original line items
      const mergedOriginalLineItems = originalData.lineItems.map((origItem) => {
        if (!origItem.account_type) {
          const currentMatch = lineItems.find((li) => li.id === origItem.id);
          return currentMatch
            ? { ...origItem, account_type: currentMatch.account_type }
            : origItem;
        }
        return origItem;
      });

      setOriginalData({
        date: originalData.date,
        referenceNumber: originalData.referenceNumber,
        currencyId: originalData.currencyId,
        locationId: originalData.locationId,
        notes: originalData.notes,
        reportingMethod: originalData.reportingMethod,
        lineItems: mergedOriginalLineItems,
      });

      // Mark as finalized - this prevents any future updates to originalData
      setOriginalDataFinalized(true);
    }
  }, [
    currenciesLoading,
    isPageLoading,
    lineItems,
    originalData,
    originalDataFinalized,
  ]);

  const handleJournalAccountSelect = (
    lineItemId: string,
    account: GLMappingEntity,
  ) => {
    const currentItem = lineItems.find((item) => item.id === lineItemId);
    const previousAccountType = currentItem?.account_type ?? "";
    const nextAccountType = account.account_type ?? previousAccountType;
    const accountTypeChanged =
      previousAccountType !== "" &&
      nextAccountType !== "" &&
      previousAccountType !== nextAccountType;

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== lineItemId) return item;
        return {
          ...item,
          account_id: account.account_id.toString(),
          account_name: account.gl_name || "",
          account_type: nextAccountType,
          ...(accountTypeChanged
            ? {
                contact_id: "",
                contact_name: "",
                customer_or_vendor: "" as const,
              }
            : {}),
        };
      }),
    );

    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_account`);
      if (accountTypeChanged) {
        clearFieldError(`lineItem_${index}_contact`);
      }
    }
  };

  // Handle contact selection
  const handleContactChange = (lineItemId: string, contact: any) => {
    const contactId = contact.zoho_customerid || contact.zoho_vendorid;
    console.log("handleContactChange called with:", { lineItemId, contactId });

    // Get account name before updating (needed for caching)
    const currentItem = lineItems.find((item) => item.id === lineItemId);
    const accountName = currentItem?.account_name || "";

    // Cache the contact for future reference
    setContactsCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(contactId, contact);
      return newCache;
    });

    // Cache the selected contact in the dropdown cache so it's always available
    // This ensures when user reopens dropdown, selected contact is visible
    if (accountName) {
      const contactType = contact.zoho_customerid ? "customer" : "vendor";
      const cacheKey = {
        accountName,
        searchTerm: "",
        limit: 20,
        offset: 0,
        currencyId,
      };
      const cached = getCached<{
        items: GroupedItems<any>[];
        hasMore?: boolean;
      }>("contacts", cacheKey);

      if (cached) {
        // Check if contact is already in cache
        let hasContact = false;
        for (const group of cached.items) {
          hasContact = group.items.some((c: any) => {
            const cId = c.zoho_customerid || c.zoho_vendorid;
            return cId === contactId;
          });
          if (hasContact) break;
        }

        if (!hasContact) {
          // Add selected contact to appropriate group
          const groupLabel =
            contactType === "customer" ? "Customers" : "Vendors";
          let targetGroup = cached.items.find(
            (g: GroupedItems<any>) => g.groupLabel === groupLabel,
          );

          if (!targetGroup) {
            targetGroup = { groupLabel, items: [], hasMore: false };
            cached.items.push(targetGroup);
          }

          // Prepend selected contact to the group
          targetGroup.items = [contact, ...targetGroup.items];

          setCached("contacts", cacheKey, cached);
        }
      }
    }

    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          const updatedItem: JournalLineItemForm = {
            ...item,
            contact_id: contactId,
            contact_name:
              contact.customer_name || contact.vendor_legal_name || "",
            customer_or_vendor: contact.zoho_customerid
              ? ("customer" as const)
              : contact.zoho_vendorid
                ? ("vendor" as const)
                : "",
            reference_id: "",
            reference_display: "",
          };

          console.log("Updated line item:", updatedItem);
          return updatedItem;
        }
        return item;
      }),
    );

    // Clear errors for this line item
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_contact`);
    }
  };

  // Clear contact (and bill/invoice reference) for a line item - for manual
  const handleClearContact = (lineItemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id === lineItemId) {
          return {
            ...item,
            contact_id: "",
            contact_name: "",
            customer_or_vendor: "" as const,
            reference_id: "",
            reference_display: "",
            reference_balance: undefined,
          };
        }
        return item;
      }),
    );
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    if (index !== -1) {
      clearFieldError(`lineItem_${index}_contact`);
    }
  };

  // Reject negative values for debit/credit (typing and paste)
  const allowOnlyNonNegative = (value: string): boolean =>
    value === "" ||
    (!value.includes("-") &&
      (Number.isNaN(parseFloat(value)) || parseFloat(value) >= 0));

  // Handle line item field change
  const handleLineItemChange = (
    lineItemId: string,
    field: keyof JournalLineItemForm,
    value: string,
  ) => {
    const index = lineItems.findIndex((item) => item.id === lineItemId);
    const isDescriptionOverLimit =
      isApArJournal &&
      field === "description" &&
      value.length > AP_AR_DESCRIPTION_MAX_LENGTH;
    const nextValue = isDescriptionOverLimit
      ? value.slice(0, AP_AR_DESCRIPTION_MAX_LENGTH)
      : value;

    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, [field]: nextValue } : item,
      ),
    );

    // Clear errors for this line item field
    if (index !== -1) {
      // Clear specific field error and amount error (since debit/credit affect it)
      if (isDescriptionOverLimit) {
        setFieldError(
          `lineItem_${index}_description`,
          `Description cannot exceed ${AP_AR_DESCRIPTION_MAX_LENGTH} characters`,
        );
      } else {
        clearFieldError(`lineItem_${index}_${field}`);
      }
      if (field === "debit" || field === "credit") {
        clearFieldError(`lineItem_${index}_amount`);
        clearFieldError("balance"); // Also clear balance error when amounts change
      }
    }
  };

  // Add new line item
  const handleAddRow = () => {
    const newId = (
      Math.max(...lineItems.map((item) => parseInt(item.id))) + 1
    ).toString();
    setLineItems((prev) => [
      ...prev,
      {
        id: newId,
        account_type: "",
        account_id: "",
        account_name: "",
        description: "",
        contact_id: "",
        contact_name: "",
        customer_or_vendor: "",
        reference_id: "",
        reference_display: "",
        debit: "",
        credit: "",
      },
    ]);
  };

  // Remove line item
  const handleRemoveRow = (lineItemId: string) => {
    if (lineItems.length > 2) {
      setLineItems((prev) => prev.filter((item) => item.id !== lineItemId));
    } else {
      showError("You must have at least 2 line items");
    }
  };

  // Calculate totals
  const getTotalDebits = (): number => {
    return lineItems.reduce((sum, item) => {
      const debit = parseFloat(item.debit) || 0;
      return sum + debit;
    }, 0);
  };

  const getTotalCredits = (): number => {
    return lineItems.reduce((sum, item) => {
      const credit = parseFloat(item.credit) || 0;
      return sum + credit;
    }, 0);
  };

  const getDifference = (): number => {
    return getTotalDebits() - getTotalCredits();
  };

  // Detect changes in non-recurring fields
  const detectChanges = (): JournalChange[] => {
    if (!originalData) return [];

    const changes: JournalChange[] = [];

    // Check date
    if (date !== originalData.date) {
      changes.push({
        field: "date",
        label: "Date",
        oldValue: new Date(originalData.date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        newValue: new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      });
    }

    // Check reference number
    if (referenceNumber !== originalData.referenceNumber) {
      changes.push({
        field: "referenceNumber",
        label: "Reference Number",
        oldValue: originalData.referenceNumber || "None",
        newValue: referenceNumber || "None",
      });
    }

    // Check currency
    if (currencyId !== originalData.currencyId) {
      const oldCurrency = currencies.find(
        (c) => c.currency_id === originalData.currencyId,
      );
      const newCurrency = currencies.find((c) => c.currency_id === currencyId);
      changes.push({
        field: "currencyId",
        label: "Currency",
        oldValue: oldCurrency
          ? `${oldCurrency.currency_code} – ${oldCurrency.currency_name}`
          : originalData.currencyId,
        newValue: newCurrency
          ? `${newCurrency.currency_code} – ${newCurrency.currency_name}`
          : currencyId,
      });
    }

    // ZOUR-65 F4: a location change is a real change — it moves the journal to
    // another GST registration, which decides the GSTIN and the tax split. It
    // has to enable the Update button and show up in the review banner.
    //
    // Compared on the RESOLVED location, not the raw value: NULL and the
    // primary's own id are the same registration (the backend folds one to the
    // other), so picking the primary back out of the dropdown after a detour
    // must read as no change rather than "Head Office -> Head Office".
    const resolveLocation = (id: string | null) =>
      activeLocations.find((candidate) => candidate.zoho_location_id === id) ??
      (id ? null : activeLocations.find((candidate) => candidate.is_primary) ?? null);

    const oldLocation = resolveLocation(originalData.locationId ?? null);
    const newLocation = resolveLocation(locationId ?? null);

    if (
      (oldLocation?.zoho_location_id ?? originalData.locationId ?? null) !==
      (newLocation?.zoho_location_id ?? locationId ?? null)
    ) {
      changes.push({
        field: "locationId",
        label: "Location",
        oldValue: oldLocation?.name ?? "Primary location",
        newValue: newLocation?.name ?? "Primary location",
      });
    }

    // Check notes
    if (notes !== originalData.notes) {
      changes.push({
        field: "notes",
        label: "Notes",
        oldValue: originalData.notes,
        newValue: notes,
      });
    }

    // Check reporting method
    if (reportingMethod !== originalData.reportingMethod) {
      const formatReportingMethod = (method: string) => {
        switch (method) {
          case "both":
            return "Accrual and Cash";
          case "cash":
            return "Cash Only";
          case "bank":
            return "Bank Only";
          default:
            return method;
        }
      };
      changes.push({
        field: "reportingMethod",
        label: "Reporting Method",
        oldValue: formatReportingMethod(originalData.reportingMethod),
        newValue: formatReportingMethod(reportingMethod),
      });
    }

    // Build fast lookup maps by id
    const originalById = new Map(
      originalData.lineItems.map((li) => [li.id, li]),
    );
    const currentById = new Map(lineItems.map((li) => [li.id, li]));

    // Detailed comparison for items that exist in both
    lineItems.forEach((currentItem, index) => {
      const originalItem = originalById.get(currentItem.id);

      if (originalItem) {
        // Check account type change
        if (currentItem.account_type !== originalItem.account_type) {
          changes.push({
            field: `lineItem_${index}_accountType`,
            label: `Line Item ${index + 1} - Account Type`,
            oldValue: originalItem.account_type
              ? formatAccountType(originalItem.account_type)
              : "Not set",
            newValue: currentItem.account_type
              ? formatAccountType(currentItem.account_type)
              : "Not set",
          });
        }

        // Check account change
        if (currentItem.account_id !== originalItem.account_id) {
          changes.push({
            field: `lineItem_${index}_account`,
            label: `Line Item ${index + 1} - Account`,
            oldValue: originalItem.account_name || "Not set",
            newValue: currentItem.account_name || "Not set",
          });
        }

        // Check description change
        if (currentItem.description !== originalItem.description) {
          changes.push({
            field: `lineItem_${index}_description`,
            label: `Line Item ${index + 1} - Description`,
            oldValue: originalItem.description || "None",
            newValue: currentItem.description || "None",
          });
        }

        // Check customer_or_vendor change (dropdown)
        if (
          currentItem.customer_or_vendor !== originalItem.customer_or_vendor
        ) {
          changes.push({
            field: `lineItem_${index}_customerOrVendor`,
            label: `Line Item ${index + 1} - Contact Type`,
            oldValue: originalItem.customer_or_vendor || "None",
            newValue: currentItem.customer_or_vendor || "None",
          });
        }

        // Check contact change (dropdown)
        if (currentItem.contact_id !== originalItem.contact_id) {
          changes.push({
            field: `lineItem_${index}_contact`,
            label: `Line Item ${index + 1} - Contact`,
            oldValue: originalItem.contact_name || "None",
            newValue: currentItem.contact_name || "None",
          });
        }

        // Check debit change
        if (currentItem.debit !== originalItem.debit) {
          changes.push({
            field: `lineItem_${index}_debit`,
            label: `Line Item ${index + 1} - Debit`,
            oldValue: originalItem.debit
              ? `${currencySymbol}${parseFloat(originalItem.debit).toFixed(2)}`
              : "0.00",
            newValue: currentItem.debit
              ? `${currencySymbol}${parseFloat(currentItem.debit).toFixed(2)}`
              : "0.00",
          });
        }

        // Check credit change
        if (currentItem.credit !== originalItem.credit) {
          changes.push({
            field: `lineItem_${index}_credit`,
            label: `Line Item ${index + 1} - Credit`,
            oldValue: originalItem.credit
              ? `${currencySymbol}${parseFloat(originalItem.credit).toFixed(2)}`
              : "0.00",
            newValue: currentItem.credit
              ? `${currencySymbol}${parseFloat(currentItem.credit).toFixed(2)}`
              : "0.00",
          });
        }
      }
    });

    // Additions: items in current but not in original
    lineItems.forEach((currentItem, index) => {
      if (!originalById.has(currentItem.id)) {
        const pretty = [
          currentItem.account_type
            ? `Type: ${formatAccountType(currentItem.account_type)}`
            : undefined,
          currentItem.account_name
            ? `Account: ${currentItem.account_name}`
            : undefined,
          currentItem.description
            ? `Desc: ${currentItem.description}`
            : undefined,
          currentItem.customer_or_vendor
            ? `Contact Type: ${currentItem.customer_or_vendor}`
            : undefined,
          currentItem.contact_name
            ? `Contact: ${currentItem.contact_name}`
            : undefined,
          (parseFloat(currentItem.debit) || 0) > 0
            ? `Debit: ${currencySymbol}${(
                parseFloat(currentItem.debit) || 0
              ).toFixed(2)}`
            : undefined,
          (parseFloat(currentItem.credit) || 0) > 0
            ? `Credit: ${currencySymbol}${(
                parseFloat(currentItem.credit) || 0
              ).toFixed(2)}`
            : undefined,
        ]
          .filter(Boolean)
          .join(" · ");

        changes.push({
          field: `lineItem_${index}_added`,
          label: `Line Item ${index + 1} - Added`,
          oldValue: "—",
          newValue: pretty || "New line added",
        });
      }
    });

    // Removals: items in original but not in current
    originalData.lineItems.forEach((origItem, origIndex) => {
      if (!currentById.has(origItem.id)) {
        const prettyOld = [
          origItem.account_type
            ? `Type: ${formatAccountType(origItem.account_type)}`
            : undefined,
          origItem.account_name
            ? `Account: ${origItem.account_name}`
            : undefined,
          origItem.description ? `Desc: ${origItem.description}` : undefined,
          origItem.customer_or_vendor
            ? `Contact Type: ${origItem.customer_or_vendor}`
            : undefined,
          origItem.contact_name
            ? `Contact: ${origItem.contact_name}`
            : undefined,
          (parseFloat(origItem.debit) || 0) > 0
            ? `Debit: ${currencySymbol}${(
                parseFloat(origItem.debit) || 0
              ).toFixed(2)}`
            : undefined,
          (parseFloat(origItem.credit) || 0) > 0
            ? `Credit: ${currencySymbol}${(
                parseFloat(origItem.credit) || 0
              ).toFixed(2)}`
            : undefined,
        ]
          .filter(Boolean)
          .join(" · ");

        changes.push({
          field: `lineItem_${origIndex}_removed`,
          label: `Line Item ${origIndex + 1} - Removed`,
          oldValue: prettyOld || "Line removed",
          newValue: "—",
        });
      }
    });

    // Also show count change for quick glance
    if (lineItems.length !== originalData.lineItems.length) {
      changes.push({
        field: "lineItemCount",
        label: "Line Item Count",
        oldValue: `${originalData.lineItems.length} items`,
        newValue: `${lineItems.length} items`,
      });
    }

    return changes;
  };

  // Check if there are any non-recurring changes
  const hasNonRecurringChanges = (): boolean => {
    return detectChanges().length > 0;
  };

  // Check if any line items contain Accounts Receivable or Accounts Payable
  // AND if the journal has been posted to Zoho
  const hasARorAPAccounts = (): boolean => {
    // Only restrict if journal is posted to Zoho
    if (!zohoJournalId) {
      return false;
    }

    return lineItems.some((item) => {
      const accountName = item.account_name.toLowerCase();
      return (
        accountName.includes("accounts receivable") ||
        accountName.includes("account receivable") ||
        accountName.includes("accounts payable") ||
        accountName.includes("account payable")
      );
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!date) newErrors.date = "Date is required";
    // Journal number is not required for Under_review (auto-generated on publish)
    // if (!journalNumber) newErrors.journalNumber = "Journal number is required";
    if (!notes || notes.trim() === "") newErrors.notes = "Notes are required";

    // Validate recurring journal fields
    if (isRecurring === "yes") {
      if (!recurringStartDate)
        newErrors.recurringStartDate =
          "Start date is required for recurring journals";
      if (!recurringFrequency)
        newErrors.recurringFrequency =
          "Frequency is required for recurring journals";
      if (recurringFrequency === "every_n_days" && !recurringInterval)
        newErrors.recurringInterval = "Number of days is required";

      // Validate start date based on frequency
      if (recurringStartDate && recurringFrequency) {
        if (!isValidStartDate(recurringStartDate, recurringFrequency)) {
          switch (recurringFrequency) {
            case "start_of_month":
              newErrors.recurringStartDate =
                "Start date must be the 1st of a month after today";
              break;
            case "end_of_month":
              newErrors.recurringStartDate =
                "Start date must be the last day of a month after today";
              break;
            case "every_n_days":
              newErrors.recurringStartDate = `Start date must be at least ${recurringInterval} days from today`;
              break;
            case "end_of_year":
              newErrors.recurringStartDate =
                "Start date must be December 31st of a future year";
              break;
            case "end_of_quarter":
              newErrors.recurringStartDate =
                "Start date must be the end of a quarter (Mar 31, Jun 30, Sep 30, or Dec 31)";
              break;
            case "date_of_month":
              newErrors.recurringStartDate =
                "Start date cannot be the 1st or last day of the month";
              break;
          }
        }
      }
    }
    if (notes.length > 500)
      newErrors.notes = "Notes cannot exceed 500 characters";

    // Validate line items
    lineItems.forEach((item, index) => {
      if (!item.account_id)
        newErrors[`lineItem_${index}_account`] = "Account is required";
      // if (!item.description || item.description.trim() === "")
      //   newErrors[`lineItem_${index}_description`] = "Description is required";
      if (
        isApArJournal &&
        item.description.length > AP_AR_DESCRIPTION_MAX_LENGTH
      ) {
        newErrors[`lineItem_${index}_description`] =
          `Description cannot exceed ${AP_AR_DESCRIPTION_MAX_LENGTH} characters`;
      }

      // Validate contact only for Accounts Payable and Accounts Receivable
      if (isContactRequired(item.account_name) && !item.contact_id) {
        newErrors[`lineItem_${index}_contact`] =
          "Contact is required for this account type";
      }

      const debit = parseFloat(item.debit) || 0;
      const credit = parseFloat(item.credit) || 0;

      if (debit === 0 && credit === 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Either debit or credit must be specified";
      }

      if (debit > 0 && credit > 0) {
        newErrors[`lineItem_${index}_amount`] =
          "Cannot have both debit and credit in the same line";
      }
    });

    // Check if debits equal credits
    const difference = getDifference();
    if (Math.abs(difference) > 0.01) {
      newErrors.balance = "Total debits must equal total credits";
    }

    // AP/AR: if a line has a bill/invoice selected, amount must not exceed its balance
    const referenceBalanceViolations: string[] = [];
    lineItems.forEach((item, index) => {
      const bal = item.reference_balance;
      if (item.reference_id && bal != null && bal > 0) {
        const amount = parseFloat(item.debit) || parseFloat(item.credit) || 0;
        if (amount > bal) {
          const lineNum = index + 1;
          const refLabel =
            item.account_type === "accounts_payable" ? "bill" : "invoice";
          referenceBalanceViolations.push(
            `Line ${lineNum}: amount exceeds ${refLabel} balance (balance: ${bal.toLocaleString()})`,
          );
        }
      }
    });
    if (referenceBalanceViolations.length > 0) {
      newErrors.referenceBalance = referenceBalanceViolations.join(". ");
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to prepare journal data for API (reusable for review requests)
  const prepareJournalData = () => {
    const apiLineItems: any[] = lineItems.map((item) => {
      const debit = parseFloat(item.debit) || 0;
      const credit = parseFloat(item.credit) || 0;

      const lineItem: any = {
        account_id: item.account_id,
        is_debit_or_credit: debit > 0 ? "debit" : "credit",
        amount: debit > 0 ? debit : credit,
        description: item.description,
        contact_id: item.contact_id || undefined,
        customer_or_vendor: item.customer_or_vendor || undefined,
      };

      // Include account_name and contact_name in raw_json for better display
      if (item.account_name) {
        lineItem.account_name = item.account_name;
      }
      if (item.contact_name) {
        lineItem.contact_name = item.contact_name;
      }

      return lineItem;
    });

    return {
      journal: {
        id: journalId,
        date,
        reference_number: referenceNumber,
        currency_id: currencyId,
        currency_symbol: currencySymbol,
        notes,
        type: reportingMethod,
        // ZOUR-65 F4: sent on every path this builder feeds. The backend folds
        // the primary's own id back to NULL (F1 `foldPrimaryLocation`).
        location_id: locationId,
      },
      line_items: apiLineItems,
      is_recurring: isRecurring === "yes",
      ...(isRecurring === "yes" && {
        recurrence_frequency: recurringFrequency,
        template_name: recurringJournalName,
        starts_on: recurringStartDate,
        ...(recurringFrequency === "every_n_days" && {
          recurrence_interval: recurringInterval,
        }),
        ...(recurringNeverExpires
          ? { never_expires: true }
          : { ends_on: recurringEndDate, never_expires: false }),
      }),
    };
  };

  // Helper function to calculate field_changes by comparing API payload against original Journal JSON
  // This ensures we compare what will actually be sent to the API, avoiding false positives
  const calculateFieldChanges = (): Array<{
    field: string;
    old: string;
    new: string;
  }> => {
    if (!originalJournalJson) {
      console.warn(
        "⚠️ calculateFieldChanges: originalJournalJson is null/undefined",
      );
      return [];
    }

    // Get the current API payload (what will be sent to the API)
    const currentPayload = prepareJournalData();
    const changes: Array<{ field: string; old: string; new: string }> = [];

    // Helper to normalize strings (handle null/undefined/empty)
    const normalizeString = (val: string | null | undefined): string => {
      if (val === null || val === undefined) return "";
      return String(val).trim();
    };

    // Helper to compare numbers (handle precision issues and string/number types)
    const compareNumbers = (
      a: number | string,
      b: number | string,
    ): boolean => {
      const numA = typeof a === "string" ? parseFloat(a) : a;
      const numB = typeof b === "string" ? parseFloat(b) : b;
      if (isNaN(numA) && isNaN(numB)) return true;
      if (isNaN(numA) || isNaN(numB)) return false;
      return Math.abs(numA - numB) < 0.01; // Allow small floating point differences
    };

    // Helper to get original recurring fields from Journal JSON
    const getOriginalRecurringFields = () => {
      if (originalJournalJson.template_id && originalJournalJson.template) {
        return {
          is_recurring: true,
          template_name: originalJournalJson.template.template_name || "",
          recurrence_frequency:
            originalJournalJson.template.recurrence_frequency || "",
          recurrence_interval:
            originalJournalJson.template.recurrence_interval || null,
          starts_on: originalJournalJson.template.starts_on || "",
          ends_on: originalJournalJson.template.ends_on || "",
          never_expires: originalJournalJson.template.never_expires || false,
        };
      }
      return {
        is_recurring: false,
        template_name: "",
        recurrence_frequency: "",
        recurrence_interval: null,
        starts_on: "",
        ends_on: "",
        never_expires: false,
      };
    };

    const originalRecurring = getOriginalRecurringFields();
    const currentIsRecurring = currentPayload.is_recurring;

    // Compare basic journal fields
    if (currentPayload.journal.date !== originalJournalJson.date) {
      changes.push({
        field: "date",
        old: originalJournalJson.date,
        new: currentPayload.journal.date,
      });
    }

    if (
      normalizeString(currentPayload.journal.reference_number) !==
      normalizeString(originalJournalJson.reference_number)
    ) {
      changes.push({
        field: "reference_number",
        old: normalizeString(originalJournalJson.reference_number),
        new: normalizeString(currentPayload.journal.reference_number),
      });
    }

    if (
      currentPayload.journal.currency_id.toString() !==
      originalJournalJson.currency_id.toString()
    ) {
      changes.push({
        field: "currency_id",
        old: originalJournalJson.currency_id.toString(),
        new: currentPayload.journal.currency_id.toString(),
      });
    }

    if (
      normalizeString(currentPayload.journal.notes) !==
      normalizeString(originalJournalJson.notes)
    ) {
      changes.push({
        field: "notes",
        old: normalizeString(originalJournalJson.notes),
        new: normalizeString(currentPayload.journal.notes),
      });
    }

    if (currentPayload.journal.type !== originalJournalJson.type) {
      changes.push({
        field: "type",
        old: originalJournalJson.type,
        new: currentPayload.journal.type,
      });
    }

    // Compare recurring journal fields
    if (currentIsRecurring !== originalRecurring.is_recurring) {
      changes.push({
        field: "is_recurring",
        old: originalRecurring.is_recurring ? "true" : "false",
        new: currentIsRecurring ? "true" : "false",
      });
    }

    if (currentIsRecurring || originalRecurring.is_recurring) {
      // Only compare recurring fields if journal is/was recurring
      // Type assertion needed because TypeScript doesn't know recurring fields exist
      const currentRecurring = currentPayload as typeof currentPayload & {
        template_name?: string;
        recurrence_frequency?: string;
        recurrence_interval?: number;
        starts_on?: string;
        ends_on?: string;
        never_expires?: boolean;
      };

      if (
        normalizeString(currentRecurring.template_name) !==
        normalizeString(originalRecurring.template_name)
      ) {
        changes.push({
          field: "template_name",
          old: normalizeString(originalRecurring.template_name),
          new: normalizeString(currentRecurring.template_name || ""),
        });
      }

      if (
        normalizeString(currentRecurring.recurrence_frequency) !==
        normalizeString(originalRecurring.recurrence_frequency)
      ) {
        changes.push({
          field: "recurrence_frequency",
          old: normalizeString(originalRecurring.recurrence_frequency),
          new: normalizeString(currentRecurring.recurrence_frequency || ""),
        });
      }

      if (
        currentRecurring.recurrence_interval !==
        originalRecurring.recurrence_interval
      ) {
        changes.push({
          field: "recurrence_interval",
          old: originalRecurring.recurrence_interval?.toString() || "",
          new: currentRecurring.recurrence_interval?.toString() || "",
        });
      }

      if (
        normalizeString(currentRecurring.starts_on) !==
        normalizeString(originalRecurring.starts_on)
      ) {
        changes.push({
          field: "starts_on",
          old: normalizeString(originalRecurring.starts_on),
          new: normalizeString(currentRecurring.starts_on || ""),
        });
      }

      const currentNeverExpires = currentRecurring.never_expires || false;
      if (currentNeverExpires !== originalRecurring.never_expires) {
        changes.push({
          field: "never_expires",
          old: originalRecurring.never_expires ? "true" : "false",
          new: currentNeverExpires ? "true" : "false",
        });
      }

      if (!currentNeverExpires) {
        // Only compare ends_on if never_expires is false
        if (
          normalizeString(currentRecurring.ends_on) !==
          normalizeString(originalRecurring.ends_on)
        ) {
          changes.push({
            field: "ends_on",
            old: normalizeString(originalRecurring.ends_on),
            new: normalizeString(currentRecurring.ends_on || ""),
          });
        }
      }
    }

    // Compare line items - SIMPLE: match by position, check ALL fields
    const originalLineItems = originalJournalJson.line_items || [];
    const currentLineItems = currentPayload.line_items || [];

    // Loop through current line items and compare with original at same position
    currentLineItems.forEach((currentItem, index) => {
      const originalItem = originalLineItems[index];

      if (originalItem) {
        // Item exists at this position - check ALL fields for changes

        // Check account_id
        const origAccountId = originalItem.account_id?.toString() || "";
        const currAccountId = currentItem.account_id?.toString() || "";
        if (origAccountId !== currAccountId) {
          changes.push({
            field: `line_items[${index}].account_id`,
            old: origAccountId,
            new: currAccountId,
          });
        }

        // Check contact_id
        const origContactId = originalItem.contact_id?.toString() || "";
        const currContactId = currentItem.contact_id?.toString() || "";
        if (origContactId !== currContactId) {
          changes.push({
            field: `line_items[${index}].contact_id`,
            old: origContactId || "(empty)",
            new: currContactId || "(empty)",
          });
        }

        // Check amount - WITH DETAILED LOGGING
        const origAmount = originalItem.amount;
        const currAmount = currentItem.amount;

        const amountsEqual = compareNumbers(origAmount, currAmount);

        if (!amountsEqual) {
          changes.push({
            field: `line_items[${index}].amount`,
            old: origAmount.toString(),
            new: currAmount.toString(),
          });
        }

        // Check debit/credit type
        if (
          originalItem.is_debit_or_credit !== currentItem.is_debit_or_credit
        ) {
          changes.push({
            field: `line_items[${index}].is_debit_or_credit`,
            old: originalItem.is_debit_or_credit,
            new: currentItem.is_debit_or_credit,
          });
        }

        // Check description
        const origDescription = normalizeString(originalItem.description);
        const currDescription = normalizeString(currentItem.description);
        if (origDescription !== currDescription) {
          changes.push({
            field: `line_items[${index}].description`,
            old: origDescription || "(empty)",
            new: currDescription || "(empty)",
          });
        }
      } else {
        // New item added at this position

        changes.push({
          field: `Line Item ${index + 1}`,
          old: "added",
          new: `${currentItem.account_name || ""} - ${currentItem.amount} (${
            currentItem.is_debit_or_credit
          })`,
        });
      }
    });

    // Check for removed items (items in original but not in current)
    originalLineItems.forEach((originalItem, index) => {
      if (index >= currentLineItems.length) {
        // This item was removed
        changes.push({
          field: `Line Item ${index + 1}`,
          old: `${originalItem.account_name || ""} - ${originalItem.amount} (${
            originalItem.is_debit_or_credit
          })`,
          new: "removed",
        });
      }
    });

    return changes;
  };

  /**
   * Build field_changes for draft publish (PUSH_TO_ZOHO from draft).
   * Same structure as calculateFieldChanges but no comparison - just append
   * all journal data from the payload as old: "", new: value.
   * Uses prepareJournalData() format to match formatJournalChangesForBanner expectations.
   */
  const getFieldChangesForDraftPublish = (): Array<{
    field: string;
    old: string;
    new: string;
  }> => {
    const payload = prepareJournalData();
    const changes: Array<{ field: string; old: string; new: string }> = [];
    const empty = "";

    // Basic journal fields (same field names as calculateFieldChanges)
    if (payload.journal.date)
      changes.push({
        field: "date",
        old: empty,
        new: payload.journal.date,
      });
    if (payload.journal.reference_number != null)
      changes.push({
        field: "reference_number",
        old: empty,
        new: String(payload.journal.reference_number || ""),
      });
    // Use currency_symbol for display (not currency_id)
    if (payload.journal.currency_symbol)
      changes.push({
        field: "currency_symbol",
        old: empty,
        new: payload.journal.currency_symbol,
      });
    if (payload.journal.notes != null)
      changes.push({
        field: "notes",
        old: empty,
        new: String(payload.journal.notes || ""),
      });
    if (payload.journal.type)
      changes.push({
        field: "type",
        old: empty,
        new: payload.journal.type,
      });

    // Recurring fields (same as calculateFieldChanges)
    const isRec = payload.is_recurring;
    changes.push({
      field: "is_recurring",
      old: empty,
      new: isRec ? "true" : "false",
    });
    if (isRec) {
      const rec = payload as typeof payload & {
        template_name?: string;
        recurrence_frequency?: string;
        recurrence_interval?: number;
        starts_on?: string;
        ends_on?: string;
        never_expires?: boolean;
      };
      if (rec.template_name != null)
        changes.push({
          field: "template_name",
          old: empty,
          new: String(rec.template_name || ""),
        });
      if (rec.recurrence_frequency != null)
        changes.push({
          field: "recurrence_frequency",
          old: empty,
          new: String(rec.recurrence_frequency || ""),
        });
      if (rec.recurrence_interval != null)
        changes.push({
          field: "recurrence_interval",
          old: empty,
          new: String(rec.recurrence_interval || ""),
        });
      if (rec.starts_on != null)
        changes.push({
          field: "starts_on",
          old: empty,
          new: String(rec.starts_on || ""),
        });
      changes.push({
        field: "never_expires",
        old: empty,
        new: rec.never_expires ? "true" : "false",
      });
      if (!rec.never_expires && rec.ends_on != null)
        changes.push({
          field: "ends_on",
          old: empty,
          new: String(rec.ends_on || ""),
        });
    }

    // Line items - SAME fields as calculateFieldChanges. Skip empty/zero - don't push (empty) or 0.00
    const items = payload.line_items || [];
    items.forEach((item: any, index: number) => {
      changes.push({
        field: `line_items[${index}].account_id`,
        old: empty,
        new: String(item.account_id ?? ""),
      });
      if (item.contact_id != null && item.contact_id !== "") {
        changes.push({
          field: `line_items[${index}].contact_id`,
          old: empty,
          new: String(item.contact_id),
        });
      }
      changes.push({
        field: `line_items[${index}].amount`,
        old: empty,
        new: String(item.amount ?? "0"),
      });
      changes.push({
        field: `line_items[${index}].is_debit_or_credit`,
        old: empty,
        new: item.is_debit_or_credit ?? "",
      });
      if (item.description != null && String(item.description).trim() !== "") {
        changes.push({
          field: `line_items[${index}].description`,
          old: empty,
          new: String(item.description),
        });
      }
    });

    return changes;
  };

  // Handle update journal
  const handleUpdateJournal = async (
    updateAllFuture: boolean = false,
    skipModal: boolean = false,
  ) => {
    // Validate form FIRST before showing any modal
    if (!validateForm()) {
      // ErrorBanner will display validation errors and auto-scroll
      return;
    }

    // // If it's a recurring journal with changes, show confirmation modal
    if (hasTemplate && hasNonRecurringChanges() && !skipModal) {
      setPendingUpdateType("update");
      setConfirmModalOpen(true);
      return;
    }

    if (!journalId) {
      showError("Journal ID is missing");
      return;
    }

    // Validate recurring journal end date
    if (isRecurring === "yes" && !recurringNeverExpires && !recurringEndDate) {
      setFieldError(
        "recurringEndDate",
        "Please either enable 'Never expires' or select an end date for the recurring journal",
      );
      return;
    }

    // When user has made the journal recurring (from non-recurring) and create_recurring requires review:
    // Show review modal first; after user submits for review we update the journal without recurring params (in handleReviewModalSuccess).
    const wasAlreadyRecurring = hasTemplate;
    const isNowRecurring = isRecurring === "yes";
    const changedToRecurring = !wasAlreadyRecurring && isNowRecurring;
    const needsReviewForRecurring =
      changedToRecurring && needsReviewForCreateRecurring;
    if (needsReviewForRecurring) {
      const journalData = prepareJournalData();
      const fieldChanges = calculateFieldChanges();
      setReviewChangeDetails({
        raw_json: journalData,
        original_json: originalJournalJson,
        field_changes: fieldChanges,
        action_types: [ACTION_CODES.CREATE_RECURRING],
      });
      setReviewActionCode(ACTION_CODES.CREATE_RECURRING);
      setPendingReviewAction("update");
      setIsReviewModalOpen(true);
      return;
    }

    // Proceed with update (no review needed, use save_as_draft status)
    await executeUpdateJournal(updateAllFuture, skipModal, "save_as_draft");
  };

  // Execute update journal (called after review approval or if no review needed)
  const executeUpdateJournal = async (
    updateAllFuture: boolean = false,
    skipModal: boolean = false,
    status: "save_as_draft" | "under_review" = "save_as_draft",
    skipNavigation: boolean = false,
    skipSuccessToast: boolean = false,
    stripRecurringFields: boolean = false,
  ): Promise<boolean> => {
    if (!journalId) {
      showError("Journal ID is missing");
      return false;
    }

    try {
      setIsLoading(true);
      let journalData = prepareJournalData();
      // When updating before a create_recurring review request, strip recurring params so the backend does not make the journal recurring yet
      if (stripRecurringFields) {
        const {
          recurrence_frequency,
          template_name,
          starts_on,
          ends_on,
          never_expires,
          recurrence_interval,
          ...rest
        } = journalData as any;
        journalData = {
          ...rest,
          is_recurring: false,
        };
      }
      // Add status and update_scope for recurring journals
      const journalDataWithScope = {
        ...journalData,
        journal: {
          ...journalData.journal,
          status: status, // Set status based on whether review is requested
        },
        ...(hasTemplate && {
          update_scope: updateAllFuture ? "all_future" : "this_only",
        }),
      };
      await journalsAPI.updateJournal(
        journalId,
        journalDataWithScope,
        attachments,
      );

      if (!skipSuccessToast) {
        showSuccess("Journal updated successfully");
      }
      if (!skipNavigation) {
        navigate(-1);
      }
      return true;
    } catch (error: any) {
      // Use centralized error parsing and display
      setApiErrors(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle update and publish (update and post to Zoho)
  const handleUpdateAndPublish = async (
    updateAllFuture: boolean = false,
    skipModal: boolean = false,
  ) => {
    // Validate form FIRST before showing any modal
    if (!validateForm()) {
      // ErrorBanner will display validation errors and auto-scroll
      return;
    }

    // Closed month: non-Admin/CFO must submit via review
    if (isMonthClosed === true && !isAdminOrCFO) {
      setPendingClosedMonthAction("publish");
      setShowClosedMonthConfirmModal(true);
      return;
    }

    // If it's a recurring journal with changes, show confirmation modal
    if (hasTemplate && hasNonRecurringChanges() && !skipModal) {
      setPendingUpdateType("publish");
      setConfirmModalOpen(true);
      return;
    }

    if (!journalId) {
      showError("Journal ID is missing");
      return;
    }

    // Validate recurring journal end date
    if (isRecurring === "yes" && !recurringNeverExpires && !recurringEndDate) {
      setFieldError(
        "recurringEndDate",
        "Please either enable 'Never expires' or select an end date for the recurring journal",
      );
      return;
    }

    // Check review requirements (always show review modal if required, even if there's an approved review)
    const needsReviewForPublish = isPostedToZoho
      ? needsReviewForUpdateAfterPush
      : needsReviewForPushToZoho;
    // Only add CREATE_RECURRING if journal was NOT already recurring (hasTemplate is false)
    // and user is now making it recurring (isRecurring === "yes")
    const wasAlreadyRecurring = hasTemplate;
    const isNowRecurring = isRecurring === "yes";
    const changedToRecurring = !wasAlreadyRecurring && isNowRecurring;
    const needsReviewForRecurring =
      changedToRecurring && needsReviewForCreateRecurring;

    // PRIORITY 2: When journal is draft - check if push without attachment needs review
    // Same as NewJournalEntry: basic push doesn't need review, no attachments, can push, this action needs review
    const hasAttachments = attachments.length > 0;
    const needsReviewForPushWithoutAttachment =
      !isPostedToZoho &&
      !needsReviewForPublish &&
      !hasAttachments &&
      canPushToZoho &&
      needsReviewForPushToZohoWithoutAttachment;
    const needsAttachmentReview = needsReviewForPushWithoutAttachment;

    // Determine action codes
    const publishActionCode = isPostedToZoho
      ? ACTION_CODES.UPDATE_AFTER_PUSH
      : ACTION_CODES.PUSH_TO_ZOHO;

    // Determine if we need to show review modal
    const needsPublishReview = needsReviewForPublish;
    const needsRecurringReview = needsReviewForRecurring;

    // ALWAYS show review modal if review is required and not explicitly approved
    if (needsPublishReview || needsAttachmentReview || needsRecurringReview) {
      // Determine primary action code and action_types array
      let primaryActionCode: string = publishActionCode;
      const actionTypes: string[] = [];

      // Priority order determines which action is primary (same as NewJournalEntry)
      if (needsPublishReview) {
        primaryActionCode = publishActionCode;
        actionTypes.push(publishActionCode);
        if (needsRecurringReview) {
          actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        }
      } else if (needsAttachmentReview) {
        primaryActionCode = ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT;
        actionTypes.push(ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT);
        if (needsRecurringReview) {
          actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        }
      } else if (needsRecurringReview) {
        primaryActionCode = ACTION_CODES.CREATE_RECURRING;
        actionTypes.push(ACTION_CODES.CREATE_RECURRING);
        // Need to do this do not remove this !!
        actionTypes.push(publishActionCode);
      }

      console.log("actionTypes", actionTypes);

      // Show review modal - DO NOT proceed with publish
      const journalData = prepareJournalData();
      // For draft publish: use "new journal" style field_changes so manager sees full journal
      // (calculateFieldChanges returns empty when no edits; formatJournalChanges needs field_changes)
      const fieldChanges = !isPostedToZoho
        ? getFieldChangesForDraftPublish()
        : calculateFieldChanges();

      setReviewChangeDetails({
        raw_json: journalData,

        original_json: originalJournalJson, // Store original JSON for getting old account/contact names
        field_changes: fieldChanges,
        action_types: actionTypes.length > 0 ? actionTypes : undefined,
      });
      setReviewActionCode(primaryActionCode);
      setPendingReviewAction("publish");
      setPendingUpdateAllFuture(updateAllFuture); // Store updateAllFuture for later use
      setIsReviewModalOpen(true);
      return; // IMPORTANT: Return here, do NOT call executeUpdateAndPublish
    }

    // Only proceed with publish if:
    // 1. No review is required, OR
    // 2. Review is explicitly approved (hasApprovedReview returned true)
    // This should only happen if review was already approved via handleReviewModalSuccess
    await executeUpdateAndPublish(updateAllFuture, skipModal);
  };

  // Execute update and publish (called after review approval or if no review needed)
  const executeUpdateAndPublish = async (
    updateAllFuture: boolean = false,
    skipModal: boolean = false,
  ) => {
    try {
      setIsLoading(true);

      const journalData = prepareJournalData();
      // Add update_scope for recurring journals
      const journalDataWithScope = {
        ...journalData,
        ...(hasTemplate && {
          update_scope: updateAllFuture ? "all_future" : "this_only",
        }),
      };

      const response = await journalsAPI.postExistingJournalToAccounting(
        journalId!,
        journalDataWithScope,
        attachments,
      );

      if (response.success && response.error_details) {
        showError(`${response.message}. ${response.error_details}`);
      } else if (response.success) {
        showSuccess(response.message || "Journal updated successfully");
      }

      navigate(-1);
    } catch (error: any) {
      console.error("Error updating and publishing journal:", error);

      // Use centralized error parsing and display
      setApiErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete journal
  const handleDeleteJournal = async () => {
    if (!journalId || !originalJournalJson) {
      showError("Journal data is missing");
      return;
    }

    try {
      // Closed month: non-Admin/CFO must submit delete for approval — show ClosedMonthConfirmModal first
      if (isMonthClosed === true && !isAdminOrCFO) {
        setPendingClosedMonthAction("delete");
        setShowClosedMonthConfirmModal(true);
        setShowDeleteModal(false);
        return;
      }

      // Check if journal is pushed to Zoho and review is required
      // const isPushedToZoho = !!zohoJournalId;

      if (
        isPostedToZoho &&
        canDeleteAfterPush &&
        needsReviewForDeleteAfterPush
      ) {
        // Prepare change details for review — show only delete action and original journal contents
        const changeDetails: ChangeDetails = {
          action_types: ["delete_after_push_to_zoho"],
          raw_json: {
            id: originalJournalJson.id,
            journal_number: originalJournalJson.journal_number,
            reference_number: originalJournalJson.reference_number,
            date: originalJournalJson.date,
            currency_id: originalJournalJson.currency_id,
            currency_symbol: originalJournalJson.currency_symbol,
            notes: originalJournalJson.notes,
            type: originalJournalJson.type,
            status: originalJournalJson.status,
            zoho_journalid: originalJournalJson.zoho_journalid,
            line_items:
              originalJournalJson.line_items?.map((item: any) => ({
                id: item.id,
                account_id: item.account_id,
                account_name: item.account_name,
                account_type: item.account_type,
                is_debit_or_credit: item.is_debit_or_credit,
                amount: item.amount,
                description: item.description,
                contact_id: item.contact_id || null,
                contact_name: item.contact_name || null,
                customer_or_vendor: item.customer_or_vendor || null,
              })) || [],
            template_id: (originalJournalJson as any).template_id || null,
            created_at: originalJournalJson.created_at,
            updated_at: originalJournalJson.updated_at,
            posted_at: (originalJournalJson as any).posted_at,
          },
          field_changes: [],
          original_json: originalJournalJson,
        };

        // Open review modal instead of deleting directly
        setReviewChangeDetails(changeDetails);
        setReviewActionCode(ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO);
        setIsReviewModalOpen(true);
        setShowDeleteModal(false); // Close the delete confirmation modal
        return;
      }

      setIsDeleting(true);
      await journalsAPI.deleteJournal(journalId);
      showSuccess("Journal deleted successfully");
      navigate("/dashboard/journal");
    } catch (error: any) {
      console.error("Error deleting journal:", error);
      const errorMessage =
        error.message || error.data?.message || "Failed to delete journal";
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Handle review modal success
  // When review is required, we ONLY submit the review request - we do NOT execute the update/publish
  // The actual update/publish will happen later after the review is approved by a reviewer
  const handleReviewModalSuccess = async () => {
    setIsReviewModalOpen(false);

    // When review was for "update + create_recurring only": modal already sent the review request.
    // Now update the existing journal without recurring params so the DB has the user's edits (line items, etc.) but not recurring yet.
    if (pendingReviewAction === "update" && journalId) {
      try {
        setIsLoading(true);
        await executeUpdateJournal(
          false,
          false,
          "save_as_draft",
          true, // skipNavigation - we navigate below
          true, // skipSuccessToast - we show success below
          true, // stripRecurringFields - do not make journal recurring yet; approver will call makeJournalRecurring
        );
      } finally {
        setIsLoading(false);
      }
      setPendingReviewAction(null);
      setReviewChangeDetails(null);
      setReviewActionCode("");
      navigate(-1);
      return;
    }

    // When review was for "publish", the modal sent the review request; we don't execute publish here (approver will do it).

    // Clear review-related state
    setPendingReviewAction(null);
    setReviewChangeDetails(null);
    setReviewActionCode("");

    // Navigate back - the review request has been submitted
    navigate(-1);
  };

  // Handle confirm update from modal
  const handleConfirmUpdate = (updateAllFuture: boolean) => {
    setConfirmModalOpen(false);
    const updateType = pendingUpdateType;
    setPendingUpdateType(null);

    if (updateType === "update") {
      handleUpdateJournal(updateAllFuture, true);
    } else if (updateType === "publish") {
      handleUpdateAndPublish(updateAllFuture, true);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const fileArray = Array.from(files);

    // Check if total files (existing + new) exceed 5
    const totalFiles =
      existingAttachments.length + attachments.length + fileArray.length;
    if (totalFiles > 5) {
      showError("You can upload a maximum of 5 files in total");
      return;
    }

    // Check each file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    for (const file of fileArray) {
      if (file.size > maxSize) {
        showError(`File "${file.name}" exceeds 10MB limit`);
        return;
      }
    }

    setAttachments((prev) => [...prev, ...fileArray]);
  };

  // Handle new file removal
  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle existing file removal (open in new tab)
  const handleViewExistingFile = (url: string) => {
    window.open(url, "_blank");
  };

  // Check if all initial data is loaded
  const isAllDataLoaded = !currenciesLoading && !isPageLoading;

  // If page is loading, show loader
  if (isPageLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      </DefaultPageLayout>
    );
  }

  // If there's a page error, show error
  if (pageError) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <IconWithBackground
              variant="error"
              icon={<FeatherAlertTriangle />}
            />
            <span className="text-heading-2 font-heading-2 text-default-font">
              Failed to Load Journal
            </span>
            <span className="text-body font-body text-subtext-color text-center max-w-md">
              {pageError}
            </span>
            <Button onClick={() => navigate(-1)}>
              Back to Journals Details
            </Button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center gap-3 border-b border-solid border-neutral-border px-8 py-4">
          <IconButton icon={<FeatherArrowLeft />} onClick={handleCancel} />
          <span className="text-body font-body text-default-font">
            Back to Journals
          </span>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 px-8 py-4 overflow-y-auto overflow-x-visible pb-16">
          <div className="flex w-full flex-col items-start gap-6 px-1 py-1">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Edit Journal Entry
            </span>
          </div>

          {/* Display validation errors with new ErrorBanner component */}
          <div className="w-full pb-2">
            <ErrorBanner
              errors={errors}
              title="Please fix the following errors:"
              autoScrollOnMount={true}
            />
          </div>

          {/* Closed month banner */}
          {isMonthClosed === true && (
            <div className="w-full pb-2">
              <ClosedMonthBanner />
            </div>
          )}

          {/* Review Status Banner */}
          {review && (
            <div className="w-full pb-2 -mt-4">
              {(review.status === "pending" ||
                review.status === "rejected" ||
                (review.status === "approved" && showApprovedBanner)) && (
                <ReviewStatusBanner
                  review={review}
                  moduleCode={MODULE_CODES.JOURNAL}
                  currentUserId={user?.id ? parseInt(user.id, 10) : null}
                  onResubmit={() => {
                    if (review.status === "rejected") {
                      setIsReviewModalOpen(true);
                    }
                  }}
                />
              )}
            </div>
          )}

          {/* AR/AP Warning Banner */}
          {hasARorAPAccounts() && (
            <div className="w-full">
              <div className="flex w-full items-start gap-4 rounded-md bg-error-50 border border-solid border-error-200 px-6 py-4">
                <IconWithBackground
                  variant="error"
                  size="medium"
                  icon={<FeatherAlertTriangle />}
                />
                <div className="flex flex-col items-start gap-2">
                  <span className="text-heading-3 font-heading-3 text-error-900">
                    Posted Journal with AR/AP Cannot Be Edited
                  </span>
                  <span className="text-body font-body text-error-700">
                    This journal has been posted to {getBackendName(accountingBackend)} and contains Accounts
                    Receivable or Accounts Payable line items. Posted journals
                    with AR/AP accounts cannot be edited.
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex w-full flex-col items-start gap-6">
            <div className="w-full items-start gap-6 grid grid-cols-2">
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Date
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={errors.date}
                  error={!!errors.date}
                  icon={<FeatherCalendar />}
                  disabled={!isAllDataLoaded}
                >
                  <TextField.Input
                    type="date"
                    placeholder="Select date"
                    value={date}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setDate(event.target.value);
                      clearFieldError("date");
                    }}
                    disabled={!isAllDataLoaded}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Journal #
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={
                    status === "under_review"
                      ? "The journal number will be auto generated after Publishing"
                      : status === "approved" && journalNumber
                        ? ""
                        : errors.journalNumber
                  }
                  error={!!errors.journalNumber}
                  disabled={true}
                >
                  <TextField.Input
                    placeholder={
                      status === "under_review" ? "Auto-generated" : ""
                    }
                    value={
                      status === "approved" && journalNumber
                        ? journalNumber
                        : ""
                    }
                    readOnly
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Reference #
                </span>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText=""
                  disabled={!isAllDataLoaded}
                >
                  <TextField.Input
                    placeholder="Optional"
                    value={referenceNumber}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setReferenceNumber(event.target.value)
                    }
                    disabled={!isAllDataLoaded}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Currency
                </span>
                <Select
                  label=""
                  placeholder={
                    !isAllDataLoaded ? "Loading..." : "Select currency"
                  }
                  value={currencyId}
                  onValueChange={(value: string) => {
                    setCurrencyId(value);

                    console.log("Currencies", currencies);
                    const currency = currencies.find(
                      (c) => c.currency_id === value,
                    );
                    if (currency) {
                      console.log("Currency", currency);
                      setCurrencySymbol(currency.currency_symbol);
                    }

                    // Clear contacts from all line items when currency changes
                    // since contacts are filtered by currency
                    setLineItems((prev) =>
                      prev.map((item) => ({
                        ...item,
                        contact_id: "",
                        contact_name: "",
                        customer_or_vendor: "",
                        reference_id: "",
                        reference_display: "",
                      })),
                    );

                    // Clear contacts cache
                    setContactsCache(new Map());

                    // Clear contacts dropdown cache
                    clearCache("contacts");
                  }}
                  disabled={!isAllDataLoaded}
                >
                  {currencies.map((currency) => (
                    <Select.Item key={currency.id} value={currency.currency_id}>
                      {currency.currency_code} – {currency.currency_name}
                    </Select.Item>
                  ))}
                </Select>
              </div>
              {/* ZOUR-65 F4: which GST registration this journal is filed
                  under. Locked once the journal is in Zoho — moving it would
                  be a different Zoho document, not an edit of this one. */}
              {hasMultipleLocations && (
                <div className="flex flex-col items-start gap-2">
                  <LocationPicker
                    locations={activeLocations}
                    value={locationId}
                    onChange={(picked) => setLocationId(picked)}
                    disabled={!isAllDataLoaded}
                    lockedReason={
                      status === "approved"
                        ? "This journal is published to Zoho. Its location cannot be changed \u2014 a journal filed under a different registration is a new document there."
                        : null
                    }
                  />
                </div>
              )}
              <div className="flex flex-col items-start gap-2 col-span-2">
                <div className="flex items-center gap-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Notes
                  </span>
                  <span className="text-body-bold font-body-bold text-error-600">
                    *
                  </span>
                </div>
                <TextField
                  className="h-auto w-full flex-none"
                  label=""
                  helpText={
                    errors.notes || `Max. 500 characters · ${notes.length}/500`
                  }
                  error={!!errors.notes}
                  disabled={!isAllDataLoaded}
                >
                  <TextField.Input
                    placeholder="Enter notes"
                    value={notes}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setNotes(event.target.value);
                      clearFieldError("notes");
                    }}
                    maxLength={500}
                    disabled={!isAllDataLoaded}
                  />
                </TextField>
              </div>
              <div className="flex flex-col items-start gap-2 col-span-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Reporting Method
                </span>
                <div className="flex gap-4">
                  <label
                    className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                      !isAllDataLoaded
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="both"
                      checked={reportingMethod === "both"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={!isAllDataLoaded}
                    />
                    <span className="text-body font-body text-default-font">
                      Accrual and Cash
                    </span>
                  </label>
                  <label
                    className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                      !isAllDataLoaded
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportingMethod"
                      value="cash"
                      checked={reportingMethod === "cash"}
                      onChange={(e) => setReportingMethod(e.target.value)}
                      className="w-4 h-4 text-blue-600"
                      disabled={!isAllDataLoaded}
                    />
                    <span className="text-body font-body text-default-font">
                      Cash Only
                    </span>
                  </label>
                </div>
              </div>
              {!hasTemplate && canCreateRecurring && (
                <div className="flex flex-col items-start gap-2 col-span-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Recurring Journal
                  </span>
                  <div className="flex gap-4">
                    <label
                      className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                        !isAllDataLoaded
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="isRecurring"
                        value="yes"
                        checked={isRecurring === "yes"}
                        onChange={(e) => setIsRecurring(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                        disabled={!isAllDataLoaded}
                      />
                      <span className="text-body font-body text-default-font">
                        Yes
                      </span>
                    </label>
                    <label
                      className={`flex items-center gap-2 p-3 border border-gray-200 rounded-md ${
                        !isAllDataLoaded
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="isRecurring"
                        value="no"
                        checked={isRecurring === "no"}
                        onChange={(e) => setIsRecurring(e.target.value)}
                        className="w-4 h-4 text-blue-600"
                        disabled={!isAllDataLoaded}
                      />
                      <span className="text-body font-body text-default-font">
                        No
                      </span>
                    </label>
                  </div>
                </div>
              )}
              {hasTemplate && (
                <div className="flex flex-col items-start gap-2 col-span-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Recurring Journal
                  </span>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-solid border-blue-200">
                    <span className="text-body font-body text-blue-700">
                      This journal is part of a recurring template and cannot be
                      changed to non-recurring.
                    </span>
                  </div>
                </div>
              )}
              {(isRecurring === "yes" || hasTemplate) && (
                <div className="flex flex-col items-start gap-4 border-l-2 border-solid border-neutral-200 pl-4 col-span-2">
                  <div className="w-full items-start gap-6 grid grid-cols-2">
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Recuring Journal Name
                      </span>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText={
                          hasTemplate
                            ? "Template information cannot be edited from here"
                            : ""
                        }
                        disabled={hasTemplate}
                      >
                        <TextField.Input
                          placeholder="Enter recurring journal name"
                          value={recurringJournalName}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => setRecurringJournalName(event.target.value)}
                          disabled={hasTemplate}
                        />
                      </TextField>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Repeat Every
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          *
                        </span>
                      </div>
                      <Select
                        label=""
                        placeholder="Select frequency"
                        value={recurringFrequency}
                        onValueChange={(value: string) => {
                          setRecurringFrequency(value);
                          clearFieldError("recurringFrequency");
                        }}
                        error={!!errors.recurringFrequency}
                        helpText={
                          errors.recurringFrequency ||
                          (hasTemplate
                            ? "Template information cannot be edited from here"
                            : "")
                        }
                        disabled={hasTemplate}
                      >
                        <Select.Item value="daily">Daily</Select.Item>
                        <Select.Item value="start_of_month">
                          Start of Month
                        </Select.Item>
                        <Select.Item value="end_of_month">
                          End of Month
                        </Select.Item>
                        <Select.Item value="end_of_quarter">
                          End of Quarter
                        </Select.Item>
                        <Select.Item value="end_of_year">
                          End of Year
                        </Select.Item>
                        <Select.Item value="date_of_month">
                          Date of Month
                        </Select.Item>
                        <Select.Item value="every_n_days">
                          Every N Days
                        </Select.Item>
                      </Select>
                    </div>
                    {recurringFrequency === "every_n_days" && (
                      <div className="flex flex-col items-start gap-2">
                        <div className="flex items-center gap-1">
                          <span className="text-body-bold font-body-bold text-default-font">
                            Number of Days
                          </span>
                          <span className="text-body-bold font-body-bold text-error-600">
                            *
                          </span>
                        </div>
                        <TextField
                          className="h-auto w-full flex-none"
                          label=""
                          helpText={
                            errors.recurringInterval ||
                            (hasTemplate
                              ? "Template information cannot be edited from here"
                              : "")
                          }
                          error={!!errors.recurringInterval}
                          disabled={hasTemplate}
                        >
                          <TextField.Input
                            type="number"
                            placeholder="Enter number of days"
                            value={recurringInterval.toString()}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              setRecurringInterval(
                                event.target.value
                                  ? parseInt(event.target.value)
                                  : "",
                              );
                              clearFieldError("recurringInterval");
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="1"
                            disabled={hasTemplate}
                          />
                        </TextField>
                      </div>
                    )}
                    <div className="flex flex-col items-start gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Starts On
                        </span>
                        <span className="text-body-bold font-body-bold text-error-600">
                          *
                        </span>
                      </div>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText={
                          errors.recurringStartDate ||
                          (hasTemplate
                            ? "Template information cannot be edited from here"
                            : getStartDateHelpText(recurringFrequency))
                        }
                        error={!!errors.recurringStartDate}
                        icon={<FeatherCalendar />}
                        disabled={hasTemplate}
                      >
                        <TextField.Input
                          type="date"
                          placeholder="Select start date"
                          value={recurringStartDate}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setRecurringStartDate(event.target.value);
                            clearFieldError("recurringStartDate");
                          }}
                          min={getMinStartDate(recurringFrequency)}
                          disabled={
                            hasTemplate ||
                            (recurringFrequency === "every_n_days" &&
                              !recurringInterval)
                          }
                        />
                      </TextField>
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Ends On
                      </span>
                      <TextField
                        className="h-auto w-full flex-none"
                        label=""
                        helpText={
                          hasTemplate
                            ? "Template information cannot be edited from here"
                            : ""
                        }
                        icon={<FeatherCalendar />}
                        disabled={recurringNeverExpires}
                      >
                        <TextField.Input
                          type="date"
                          placeholder="Select end date"
                          value={recurringEndDate}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            setRecurringEndDate(event.target.value);
                            clearFieldError("recurringEndDate");
                          }}
                          disabled={recurringNeverExpires}
                          min={new Date().toISOString().split("T")[0]}
                        />
                      </TextField>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 flex-none rounded-md border border-solid border-neutral-300"
                      checked={recurringNeverExpires}
                      onChange={(e) =>
                        setRecurringNeverExpires(e.target.checked)
                      }
                      disabled={hasTemplate}
                    />
                    <span
                      className={`text-body font-body ${
                        hasTemplate ? "text-neutral-400" : "text-default-font"
                      }`}
                    >
                      Never expires
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-6">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Line Items
            </span>
            <div className="flex w-full flex-col items-start rounded-md border border-solid border-neutral-border overflow-x-auto overflow-y-visible">
              <div className="flex items-center gap-3 border-b border-solid border-neutral-border bg-neutral-50 px-4 py-3 min-w-full">
                <div className="flex w-8 flex-none items-center">
                  <span className="text-body-bold font-body-bold text-default-font">
                    #
                  </span>
                </div>
                <div className="flex w-80 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Account*
                  </span>
                </div>
                <div className="flex w-52 flex-none flex-col items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Description
                  </span>
                  {isApArJournal && (
                    <span className="mt-0.5 text-[10px] leading-3 text-subtext-color">
                      Max {AP_AR_DESCRIPTION_MAX_LENGTH} characters
                    </span>
                  )}
                </div>
                <div className="flex w-40 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Contact ({currencySymbol})
                  </span>
                </div>
                {showBillInvoiceColumn && (
                  <div className="flex w-40 flex-none items-start">
                    <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                      Bill / Invoice
                    </span>
                  </div>
                )}
                <div className="flex w-28 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Debits
                  </span>
                </div>
                <div className="flex w-28 flex-none items-start">
                  <span className="text-body-bold font-body-bold text-default-font truncate text-sm">
                    Credits
                  </span>
                </div>
                <div className="flex w-16 flex-none items-start" />
              </div>

              {lineItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 border-b border-solid ${
                    hasLineItemError(index)
                      ? "border-error-200 bg-error-50"
                      : "border-neutral-100 bg-white"
                  } px-4 py-2.5 min-w-full overflow-visible`}
                >
                  <div className="flex w-8 flex-none items-center">
                    <span className="text-body-bold font-body-bold text-subtext-color text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <div className="w-80 flex-none flex items-center overflow-hidden min-h-[40px]">
                    {isResolvingLineItemAccountTypes ? (
                      <div className="flex h-10 w-full items-center justify-center rounded-md border border-neutral-border bg-neutral-50">
                        <Loader />
                      </div>
                    ) : (
                      <AccountNameDropdown
                        docType="journal"
                        journalType="manual"
                        selectedAccountId={item.account_id}
                        selectedAccountName={item.account_name}
                        onSelect={(account) =>
                          handleJournalAccountSelect(item.id, account)
                        }
                        disabled={!isAllDataLoaded}
                        dropdownClassName="w-[var(--radix-dropdown-menu-trigger-width)] min-w-full"
                      />
                    )}
                  </div>
                  <div className="flex w-52 flex-none items-center min-h-[40px]">
                    <TextField
                      label=""
                      helpText={errors[`lineItem_${index}_description`]}
                      error={!!errors[`lineItem_${index}_description`]}
                    >
                      <TextField.Input
                        className="text-sm"
                        placeholder={
                          isApArJournal
                            ? `Enter description (max ${AP_AR_DESCRIPTION_MAX_LENGTH})`
                            : "Enter description"
                        }
                        value={item.description}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          handleLineItemChange(
                            item.id,
                            "description",
                            event.target.value,
                          )
                        }
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="w-40 flex-none flex items-center gap-1 overflow-hidden min-h-[40px]">
                    <div className="flex-1 min-w-0">
                      <SearchableDropdown
                        fetchItems={(searchTerm, limit, offset) =>
                          fetchGroupedContacts(
                            item.account_name,
                            searchTerm,
                            limit,
                            offset,
                            item.contact_id,
                            item.contact_name,
                            item.customer_or_vendor as
                              | "customer"
                              | "vendor"
                              | undefined,
                          )
                        }
                        trigger={
                          <Button
                            variant="neutral-secondary"
                            className="w-full justify-between"
                            iconRight={<FeatherChevronDown />}
                            disabled={!item.account_id || !isAllDataLoaded}
                            title={
                              getContactDisplayName(item.contact_id, item) !==
                              "Select contact"
                                ? getContactDisplayName(item.contact_id, item)
                                : ""
                            }
                          >
                            <span className="text-left block max-w-full overflow-hidden whitespace-nowrap text-sm">
                              {truncateText(
                                getContactDisplayName(item.contact_id, item),
                                12,
                              )}
                            </span>
                          </Button>
                        }
                        getSearchableText={(contact: any) =>
                          contact.customer_name || contact.vendor_legal_name
                        }
                        renderItem={(contact: any) =>
                          contact.customer_name || contact.vendor_legal_name
                        }
                        getItemKey={(contact: any) =>
                          contact.zoho_customerid || contact.zoho_vendorid
                        }
                        onSelect={(contact: any) => {
                          handleContactChange(item.id, contact);
                        }}
                        selectedKey={item.contact_id}
                        searchPlaceholder="Search contacts..."
                        emptyMessage="No contacts available"
                        noResultsMessage="No contacts match your search"
                        initialLimit={20}
                      />
                    </div>
                    {item.contact_id ? (
                      <IconButton
                        variant="neutral-secondary"
                        size="small"
                        icon={<FeatherX />}
                        onClick={() => handleClearContact(item.id)}
                        title="Clear contact"
                        aria-label="Clear contact"
                        className="flex-none shrink-0"
                      />
                    ) : null}
                  </div>
                  <div className="flex w-28 flex-none items-center min-h-[40px]">
                    <TextField label="" helpText="">
                      <TextField.Input
                        type="number"
                        placeholder="0.00"
                        value={item.debit}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const v = event.target.value;
                          if (!allowOnlyNonNegative(v)) return;
                          handleLineItemChange(item.id, "debit", v);
                          if (v && parseFloat(v) > 0) {
                            handleLineItemChange(item.id, "credit", "");
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        step="0.01"
                        min="0"
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="flex w-28 flex-none items-center min-h-[40px]">
                    <TextField label="" helpText="">
                      <TextField.Input
                        type="number"
                        placeholder="0.00"
                        value={item.credit}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const v = event.target.value;
                          if (!allowOnlyNonNegative(v)) return;
                          handleLineItemChange(item.id, "credit", v);
                          if (v && parseFloat(v) > 0) {
                            handleLineItemChange(item.id, "debit", "");
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()}
                        step="0.01"
                        min="0"
                        disabled={!item.account_id || !isAllDataLoaded}
                      />
                    </TextField>
                  </div>
                  <div className="flex w-16 flex-none items-center justify-center min-h-[40px]">
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                        handleRemoveRow(item.id)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button
              className="h-auto w-auto flex-none"
              variant="neutral-tertiary"
              icon={<FeatherPlus />}
              onClick={handleAddRow}
              disabled={!isAllDataLoaded}
            >
              Add New Row
            </Button>
          </div>

          {/* Error message for balance */}
          {errors.balance && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.balance}
              </span>
            </div>
          )}
          {/* Error when line amount exceeds selected bill/invoice balance */}
          {errors.referenceBalance && (
            <div className="flex w-full items-center gap-2 px-4 py-3 rounded-md bg-error-50 border border-solid border-error-200">
              <FeatherX className="h-4 w-4 text-error-600" />
              <span className="text-body font-body text-error-700">
                {errors.referenceBalance}
              </span>
            </div>
          )}

          <div className="flex w-full items-start justify-end">
            <div className="flex w-96 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
              <span className="text-body-bold font-body-bold text-default-font">
                Summary
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Debits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalDebits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Sub Total - Credits
                  </span>
                  <span className="text-body font-body text-default-font">
                    {currencySymbol} {getTotalCredits().toFixed(2)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Total ({currencySymbol})
                  </span>
                  <div className="flex items-center gap-4">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalDebits().toFixed(2)}
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getTotalCredits().toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Difference
                  </span>
                  <span
                    className={`text-body-bold font-body-bold ${
                      Math.abs(getDifference()) < 0.01
                        ? "text-success-600"
                        : "text-error-600"
                    }`}
                  >
                    {currencySymbol} {Math.abs(getDifference()).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Attachments
            </span>
            <div className="flex items-center gap-3">
              <input
                type="file"
                id="file-upload-edit"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept="*"
              />
              <label htmlFor="file-upload-edit">
                <Button
                  className="h-auto w-auto flex-none"
                  variant="neutral-secondary"
                  iconRight={<FeatherPlus />}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    document.getElementById("file-upload-edit")?.click();
                  }}
                  disabled={
                    existingAttachments.length + attachments.length >= 5
                  }
                >
                  Upload File
                </Button>
              </label>
              <span className="text-caption font-caption text-subtext-color">
                You can upload a maximum of 5 files, 10MB each (
                {existingAttachments.length + attachments.length}/5 files)
              </span>
            </div>

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-blue-50 p-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  Existing Files ({existingAttachments.length})
                </span>
                {existingAttachments.map((file, index) => (
                  <div
                    key={`existing-${index}`}
                    className="flex w-full items-center justify-between rounded bg-white px-3 py-2 border border-solid border-neutral-100 cursor-pointer hover:bg-neutral-50"
                    onClick={() => handleViewExistingFile(file.signed_url)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-body font-body text-default-font truncate">
                        {file.filename}
                      </span>
                      <span className="text-caption font-caption text-subtext-color flex-none">
                        ({file.mime_type})
                      </span>
                    </div>
                    <span className="text-caption font-caption text-brand-600 flex-none">
                      View
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* New Attachments */}
            {attachments.length > 0 && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4">
                <span className="text-body-bold font-body-bold text-default-font">
                  New Files ({attachments.length})
                </span>
                {attachments.map((file, index) => (
                  <div
                    key={`new-${index}`}
                    className="flex w-full items-center justify-between rounded bg-white px-3 py-2 border border-solid border-neutral-100"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-body font-body text-default-font truncate">
                        {file.name}
                      </span>
                      <span className="text-caption font-caption text-subtext-color flex-none">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <IconButton
                      size="small"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveFile(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border pt-6">
            <Button variant="neutral-tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            {!isPostedToAccounting && (
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    handleUpdateJournal(false);
                  }}
                  disabled={
                    !isAllDataLoaded ||
                    isLoading ||
                    hasARorAPAccounts() ||
                    hasPendingReview ||
                    (!hasNonRecurringChanges() &&
                      !(isRecurring === "yes" && !hasTemplate)) ||
                    !canUpdate
                  }
                >
                  {isLoading ? "Updating..." : "Update Journal"}
                </Button>
                {!canUpdate && (
                  <Tooltip content="You do not have permission to perform this action">
                    <FeatherLock className="h-4 w-4 text-neutral-400" />
                  </Tooltip>
                )}
              </div>
            )}
            <Button
              variant="destructive-primary"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                setShowDeleteModal(true);
              }}
              disabled={!isAllDataLoaded || isLoading || isDeleting}
            >
              <FeatherTrash2 className="h-4 w-4" />
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  handleUpdateAndPublish(false);
                }}
                disabled={
                  !isAllDataLoaded ||
                  isLoading ||
                  isReviewDataLoading ||
                  hasARorAPAccounts() ||
                  hasPendingReview ||
                  !(isPostedToAccounting ? canUpdateAfterPush : canPushToZoho)
                }
              >
                {isLoading
                  ? isPostedToAccounting
                    ? "Updating..."
                    : "Publishing..."
                  : isPostedToAccounting
                    ? accountingBackend === "tally"
                      ? "Update Journal"
                      : "Update and Publish"
                    : accountingBackend === "tally"
                      ? getPostButtonLabel(accountingBackend)
                      : "Save and Publish"}
              </Button>
              {!(isPostedToAccounting ? canUpdateAfterPush : canPushToZoho) && (
                <Tooltip content="You do not have permission to perform this action">
                  <FeatherLock className="h-4 w-4 text-neutral-400" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Edit Modal */}
      <ConfirmEditJournalModal
        open={confirmModalOpen}
        onOpenChange={setConfirmModalOpen}
        changes={detectChanges()}
        onConfirm={handleConfirmUpdate}
        loading={isLoading}
      />

      {/* Approved Changes Modal */}
      {review && hasPendingReview && (
        <ApprovedChangesModal
          open={approvedChangesModalOpen}
          review={review}
          onApply={handleApplyApprovedChanges}
          onClose={() => setApprovedChangesModalOpen(false)}
        />
      )}

      {/* Closed month confirm modal - when non-Admin/CFO tries to save, publish, or delete in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={(open) => {
          setShowClosedMonthConfirmModal(open);
          if (!open) setPendingClosedMonthAction(null);
        }}
        entityLabel="Journal"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          const action = pendingClosedMonthAction;
          setPendingClosedMonthAction(null);

          if (action === "delete" && originalJournalJson) {
            setReviewChangeDetails({
              closed_month: true,
              action_types: [
                "delete_after_push_to_zoho",
                ACTION_TYPE_MONTH_CLOSE,
              ],
              raw_json: {
                id: originalJournalJson.id,
                journal_number: originalJournalJson.journal_number,
                reference_number: originalJournalJson.reference_number,
                date: originalJournalJson.date,
                currency_id: originalJournalJson.currency_id,
                currency_symbol: originalJournalJson.currency_symbol,
                notes: originalJournalJson.notes,
                type: originalJournalJson.type,
                status: originalJournalJson.status,
                zoho_journalid: originalJournalJson.zoho_journalid,
                line_items:
                  originalJournalJson.line_items?.map((item: any) => ({
                    id: item.id,
                    account_id: item.account_id,
                    account_name: item.account_name,
                    account_type: item.account_type,
                    is_debit_or_credit: item.is_debit_or_credit,
                    amount: item.amount,
                    description: item.description,
                    contact_id: item.contact_id || null,
                    contact_name: item.contact_name || null,
                    customer_or_vendor: item.customer_or_vendor || null,
                  })) || [],
                template_id: (originalJournalJson as any).template_id || null,
                created_at: originalJournalJson.created_at,
                updated_at: originalJournalJson.updated_at,
                posted_at: (originalJournalJson as any).posted_at,
              },
              field_changes: [],
              original_json: originalJournalJson,
            });
            setReviewActionCode(ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO);
            setPendingReviewAction(null);
            setIsReviewModalOpen(true);
            return;
          }

          if (!action) return;

          const journalData = prepareJournalData();
          // For draft publish: use "new journal" style field_changes so manager sees full journal
          const fieldChanges =
            !isPostedToZoho && action === "publish"
              ? getFieldChangesForDraftPublish()
              : calculateFieldChanges();
          const actionCode = isPostedToZoho
            ? ACTION_CODES.UPDATE_AFTER_PUSH
            : ACTION_CODES.PUSH_TO_ZOHO;
          const actionType = isPostedToZoho
            ? "update_after_push"
            : "push_to_zoho";
          setReviewChangeDetails({
            closed_month: true,
            action_types: [actionType, ACTION_TYPE_MONTH_CLOSE],
            field_changes: fieldChanges,
            raw_json: journalData,
            original_json: originalJournalJson,
          });
          setReviewActionCode(actionCode);
          setPendingReviewAction(action === "publish" ? action : null);
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode={MODULE_CODES.JOURNAL}
        actionCode={reviewActionCode}
        entityType="journal"
        entityId={journalId ? parseInt(journalId) : 0}
        documentNumber={journalNumber || undefined}
        changeDetails={reviewChangeDetails || undefined}
        onSuccess={handleReviewModalSuccess}
        skipNavigation={true} // Don't navigate, we'll handle it in onSuccess
      />

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <Dialog.Content className="min-w-[480px] max-w-[500px]">
          {/* Header */}
          <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-500">
                <FeatherTrash2 className="h-6 w-6 text-white" />
              </div>
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Delete Journal
                </span>
                <span className="text-body font-body text-subtext-color">
                  Are you sure you want to delete this journal?
                </span>
              </div>
            </div>
            <IconButton
              size="small"
              icon={<FeatherX />}
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            />
          </div>

          {/* Content */}
          <div className="flex w-full flex-col gap-6 p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-error-100">
                <FeatherAlertTriangle className="h-4 w-4 text-error-600" />
              </div>
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Warning
                </span>
                <span className="text-body font-body text-subtext-color">
                  {zohoJournalId || isPostedToTally
                    ? `This will delete the journal from both your database and ${getBackendName(accountingBackend)}. This action cannot be undone.`
                    : "This will delete the journal from your database. This action cannot be undone."}
                </span>
              </div>
            </div>

            <div className="flex w-full items-center justify-end gap-3">
              <Button
                variant="neutral-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleDeleteJournal}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Journal"}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </DefaultPageLayout>
  );
};

export default EditJournalPage;
